// Pixel art as text: a sprite is SIZE rows of SIZE marks, each mark a key into its palette and
// "." clear. Each digger has its own mass — shoulders, waist, hips, boots — and a head that
// only that digger wears. Light falls from the upper left. The planted wait shovel is a thin
// shaft; every held tool is one length.

export const SIZE = 80;

export type Palette = Readonly<Record<string, string>>;

export type Sprite = {
  readonly rows: ReadonlyArray<string>;
  readonly palette: Palette;
};

export type Character = "rusty" | "mina" | "dobbs" | "pip";

export const CHARACTERS: ReadonlyArray<Character> = ["rusty", "mina", "dobbs", "pip"];

export type Frames = {
  readonly wait: ReadonlyArray<Sprite>;
  readonly ready: ReadonlyArray<Sprite>;
  readonly walk: ReadonlyArray<Sprite>;
  readonly digDown: ReadonlyArray<Sprite>;
  readonly digLeft: ReadonlyArray<Sprite>;
  readonly digRight: ReadonlyArray<Sprite>;
};

const SHARED: Palette = {
  k: "#22182c",
  w: "#8a5a28",
  W: "#5a3818",
  m: "#c4cce0",
  n: "#6e7890",
  M: "#eef2fa",
  N: "#3c4458",
  t: "#fff4dc",
  D: "#b07848",
  Q: "#4a2814",
  o: "#d4a060",
};

type Look = {
  readonly skin: readonly [string, string, string];
  readonly hair: readonly [string, string, string];
  readonly shirt: readonly [string, string, string, string];
  readonly pants: readonly [string, string, string];
  readonly boots: readonly [string, string];
  readonly accent: readonly [string, string, string];
  readonly light: string;
};

type Build = {
  readonly shoulder: number;
  readonly waist: number;
  readonly hip: number;
  readonly boot: number;
  readonly rise: number;
  readonly hunch: number;
  readonly headRx: number;
  readonly headRy: number;
  readonly headShift: number;
  readonly arm: number;
  readonly chest: number;
  readonly thigh: number;
};

const BUILD: Readonly<Record<Character, Build>> = {
  rusty: {
    shoulder: 16,
    waist: 12,
    hip: 14,
    boot: 7,
    rise: 0,
    hunch: 0,
    headRx: 13,
    headRy: 12,
    headShift: 0,
    arm: 6,
    chest: 18,
    thigh: 18,
  },
  mina: {
    shoulder: 4,
    waist: 4,
    hip: 8,
    boot: 4,
    rise: 0,
    hunch: 0,
    headRx: 9,
    headRy: 11,
    headShift: 0,
    arm: 4,
    chest: 12,
    thigh: 30,
  },
  dobbs: {
    shoulder: 14,
    waist: 16,
    hip: 18,
    boot: 6,
    rise: 2,
    hunch: 10,
    headRx: 11,
    headRy: 10,
    headShift: 6,
    arm: 5,
    chest: 17,
    thigh: 13,
  },
  pip: {
    shoulder: 6,
    waist: 6,
    hip: 9,
    boot: 4,
    rise: 17,
    hunch: 0,
    headRx: 12,
    headRy: 13,
    headShift: 0,
    arm: 4,
    chest: 10,
    thigh: 5,
  },
};

const palette = (look: Look): Palette => ({
  ...SHARED,
  s: look.skin[0],
  d: look.skin[1],
  S: look.skin[2],
  h: look.hair[0],
  i: look.hair[1],
  H: look.hair[2],
  f: look.shirt[0],
  c: look.shirt[1],
  e: look.shirt[2],
  C: look.shirt[3],
  p: look.pants[0],
  q: look.pants[1],
  P: look.pants[2],
  b: look.boots[0],
  B: look.boots[1],
  a: look.accent[0],
  A: look.accent[1],
  Z: look.accent[2],
  l: look.light,
});

const LOOKS: Readonly<Record<Character, Look>> = {
  rusty: {
    skin: ["#e6b08a", "#c27a63", "#8a4a4a"],
    hair: ["#c9522a", "#953a24", "#5c2418"],
    shirt: ["#7cc07e", "#4a9a58", "#2f6f52", "#1f4a3c"],
    pants: ["#6b4f3a", "#4a3529", "#2e1f18"],
    boots: ["#4a3424", "#2a1c12"],
    accent: ["#dcd6c6", "#a9a396", "#5e5a56"],
    light: "#f4cf4a",
  },
  mina: {
    skin: ["#f2c9a6", "#d39a7e", "#9a5c58"],
    hair: ["#3a3450", "#262038", "#171326"],
    shirt: ["#f3ecd8", "#dcd3bf", "#b0a693", "#746a5a"],
    pants: ["#3d63b0", "#2c4488", "#1c2a5c"],
    boots: ["#5a3e2a", "#33221a"],
    accent: ["#d84040", "#a02c30", "#5e1a20"],
    light: "#f6f2e8",
  },
  dobbs: {
    skin: ["#8e5b3c", "#6a3f2c", "#3f2320"],
    hair: ["#9c9c9c", "#6f6f78", "#3c3c44"],
    shirt: ["#a97be0", "#7c4fb4", "#573684", "#33205a"],
    pants: ["#565668", "#3b3b4c", "#232330"],
    boots: ["#2e2e3c", "#1a1a26"],
    accent: ["#c9a24a", "#8f6d2c", "#4f3a16"],
    light: "#7fd8e8",
  },
  pip: {
    skin: ["#f7d7b5", "#d9a988", "#a06a5a"],
    hair: ["#f0d35a", "#c8a23a", "#7a5c1e"],
    shirt: ["#ffb070", "#ef7d3a", "#c0522e", "#6e2a18"],
    pants: ["#2f7070", "#1f4d52", "#12303a"],
    boots: ["#4a3624", "#2a1e12"],
    accent: ["#3f74dc", "#2a4f9e", "#172c5c"],
    light: "#f6f2e8",
  },
};

type Grid = Array<Array<string>>;

const sheet = (): Grid =>
  Array.from({ length: SIZE }, () => Array.from({ length: SIZE }, () => "."));

const freeze = (grid: Grid): ReadonlyArray<string> => grid.map((row) => row.join(""));

const plot = (grid: Grid, x: number, y: number, mark: string): void => {
  if (x < 0 || y < 0 || x >= SIZE || y >= SIZE) {
    return;
  }
  grid[y][x] = mark;
};

const fill = (grid: Grid, x0: number, y0: number, x1: number, y1: number, mark: string): void => {
  const left = Math.min(x0, x1);
  const right = Math.max(x0, x1);
  const top = Math.min(y0, y1);
  const bottom = Math.max(y0, y1);
  for (let y = top; y <= bottom; y++) {
    for (let x = left; x <= right; x++) {
      plot(grid, x, y, mark);
    }
  }
};

const fillPoly = (
  grid: Grid,
  pts: ReadonlyArray<readonly [number, number]>,
  mark: string,
): void => {
  const ys = pts.map((pt) => pt[1]);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  for (let y = minY; y <= maxY; y++) {
    const nodes: Array<number> = [];
    for (let i = 0; i < pts.length; i++) {
      const [x0, y0] = pts[i];
      const next = pts[(i + 1) % pts.length];
      const x1 = next[0];
      const y1 = next[1];
      if ((y0 <= y && y1 > y) || (y1 <= y && y0 > y)) {
        nodes.push(Math.round(x0 + ((y - y0) * (x1 - x0)) / (y1 - y0)));
      }
    }
    nodes.sort((a, b) => a - b);
    for (let i = 0; i + 1 < nodes.length; i += 2) {
      fill(grid, nodes[i], y, nodes[i + 1], y, mark);
    }
  }
};

const disk = (grid: Grid, cx: number, cy: number, rx: number, ry: number, mark: string): void => {
  for (let y = cy - ry; y <= cy + ry; y++) {
    for (let x = cx - rx; x <= cx + rx; x++) {
      const dx = (x - cx) / rx;
      const dy = (y - cy) / ry;
      if (dx * dx + dy * dy <= 1) {
        plot(grid, x, y, mark);
      }
    }
  }
};

const line = (
  grid: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  mark: string,
  thick: number,
): void => {
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  const half = Math.floor(thick / 2);
  for (;;) {
    for (let oy = -half; oy <= half; oy++) {
      for (let ox = -half; ox <= half; ox++) {
        plot(grid, x + ox, y + oy, mark);
      }
    }
    if (x === x1 && y === y1) {
      break;
    }
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
  }
};

const shirtMark = (x: number, left: number, right: number): string => {
  const span = Math.max(1, right - left);
  const t = (x - left) / span;
  if (t < 0.18) {
    return "f";
  }
  if (t < 0.48) {
    return "c";
  }
  if (t < 0.74) {
    return "e";
  }
  return "C";
};

const pantsMark = (x: number, left: number, right: number): string => {
  const span = Math.max(1, right - left);
  const t = (x - left) / span;
  if (t < 0.32) {
    return "p";
  }
  if (t < 0.68) {
    return "q";
  }
  return "P";
};

const BODY = new Set(["s", "d", "S", "h", "i", "H", "f", "c", "e", "C", "p", "q", "P", "b", "B"]);

const inkEdge = (grid: Grid): void => {
  const rim: Array<readonly [number, number]> = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== ".") {
        continue;
      }
      const touch =
        BODY.has(grid[y - 1]?.[x] ?? ".") ||
        BODY.has(grid[y + 1]?.[x] ?? ".") ||
        BODY.has(grid[y][x - 1] ?? ".") ||
        BODY.has(grid[y][x + 1] ?? ".");
      if (touch) {
        rim.push([x, y]);
      }
    }
  }
  for (const [x, y] of rim) {
    plot(grid, x, y, "k");
  }
};

const joint = (grid: Grid, x: number, y: number, mark: string): void => {
  fill(grid, x - 1, y - 1, x + 1, y + 1, mark);
};

const limb = (
  grid: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  thick: number,
  mark: string,
): void => {
  line(grid, x0, y0, x1, y1, mark, Math.min(4, Math.max(2, thick)));
  joint(grid, x0, y0, mark);
  joint(grid, x1, y1, mark);
};

const shaft = (
  grid: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  _thick: number,
): void => {
  line(grid, x0, y0, x1, y1, "w", 2);
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.max(1, Math.hypot(dx, dy));
  const nx = -dy / len;
  const ny = dx / len;
  line(
    grid,
    Math.round(x0 + nx),
    Math.round(y0 + ny),
    Math.round(x1 + nx),
    Math.round(y1 + ny),
    "W",
    1,
  );
};

const BLADE_DOWN: ReadonlyArray<string> = [
  ".......wWw......",
  "......nWWWn.....",
  ".....nNmMMnN....",
  "....nNmmMMmnN...",
  "...nNmmmMMmnN...",
  "...nNmmmmmmnN...",
  "..nNmmmmmmmnN...",
  ".nNNNNNNNNNNn...",
  "..nNnnnnnnnN....",
  "...nNnnnnnN.....",
];

const BLADE_LEFT: ReadonlyArray<string> = [
  ".........nnwWw.",
  ".......nNNnwWw.",
  "......nNmMmnN..",
  ".....nNmmMmnN..",
  "....nNmmmMmnN..",
  "...nNmmmmMmnN..",
  "..nNmmmmmMmnN..",
  ".nNNNNNNNNNn...",
  "..nNnnnnnnN....",
  "...nNnnnnN.....",
];

const stamp = (grid: Grid, rows: ReadonlyArray<string>, x: number, y: number): void => {
  rows.forEach((row, i) => {
    for (let c = 0; c < row.length; c++) {
      const mark = row[c];
      if (mark !== ".") {
        plot(grid, x + c, y + i, mark);
      }
    }
  });
};

const fist = (grid: Grid, x: number, y: number, _toward: number, _slim: boolean): void => {
  fill(grid, x, y, x + 1, y + 1, "s");
  plot(grid, x, y + 1, "d");
};

const sign = (value: number): number => {
  if (value > 0) {
    return 1;
  }
  if (value < 0) {
    return -1;
  }
  return 0;
};

const elbowCorner = (grid: Grid, x: number, y: number, towardX: number, towardY: number): void => {
  fill(grid, x - 1, y - 1, x + 1, y + 1, "d");
  const ox = sign(towardX);
  const oy = sign(towardY);
  plot(grid, x + ox, y, "d");
  plot(grid, x, y + oy, "d");
  plot(grid, x + ox, y + oy, "S");
  fill(grid, x - 1, y, x + 1, y, "k");
};

const arm = (
  grid: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  _thick: number,
  sleeved: boolean,
  character: Character,
): void => {
  const slim = character === "pip";
  const bulky = character === "dobbs";
  const handX = x2;
  const handY = y2;
  const upper = sleeved ? "c" : "s";
  let width = 3;
  if (slim) {
    width = 2;
  } else if (bulky) {
    width = 3;
  }
  line(grid, x0, y0, x1, y1, upper, width);
  joint(grid, x0, y0, sleeved ? "f" : upper);
  elbowCorner(grid, x1, y1, x2 - x1, y2 - y1);
  line(grid, x1, y1, handX, handY, "d", 2);
  joint(grid, handX, handY, "d");
  fist(grid, handX, handY, handX >= x1 ? 1 : -1, slim);
};

const grip = (grid: Grid, shaftX: number, y: number): void => {
  for (let gy = y; gy <= y + 3; gy++) {
    plot(grid, shaftX - 1, gy, "s");
    plot(grid, shaftX + 2, gy, "s");
    plot(grid, shaftX, gy, "w");
    plot(grid, shaftX + 1, gy, "W");
  }
  plot(grid, shaftX - 2, y + 1, "d");
  plot(grid, shaftX - 2, y + 2, "s");
  plot(grid, shaftX + 3, y + 1, "d");
  plot(grid, shaftX + 3, y + 2, "s");
  plot(grid, shaftX - 3, y + 2, "S");
  plot(grid, shaftX + 4, y + 2, "S");
  plot(grid, shaftX - 1, y + 3, "d");
  plot(grid, shaftX + 2, y + 3, "S");
};

const ferrule = (grid: Grid, x: number, y: number): void => {
  fill(grid, x, y, x + 3, y + 2, "W");
  plot(grid, x + 1, y + 1, "w");
};

const WOOD = new Set(["w", "W"]);
const METAL = new Set(["M", "m", "n", "N"]);

const joinTool = (grid: Grid): void => {
  const gaps: Array<readonly [number, number, string]> = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (!WOOD.has(grid[y][x])) {
        continue;
      }
      const dirs: ReadonlyArray<readonly [number, number]> = [
        [0, 1],
        [0, -1],
        [1, 0],
        [-1, 0],
      ];
      for (const [dx, dy] of dirs) {
        const mid = grid[y + dy]?.[x + dx];
        const far = grid[y + dy * 2]?.[x + dx * 2];
        if (mid === "." && far !== undefined && METAL.has(far)) {
          gaps.push([x + dx, y + dy, grid[y][x]]);
        }
        const farther = grid[y + dy * 3]?.[x + dx * 3];
        if (mid === "." && far === "." && farther !== undefined && METAL.has(farther)) {
          gaps.push([x + dx, y + dy, grid[y][x]]);
          gaps.push([x + dx * 2, y + dy * 2, grid[y][x]]);
        }
      }
    }
  }
  for (const [x, y, mark] of gaps) {
    plot(grid, x, y, mark);
  }
};

const boot = (grid: Grid, cx: number, cy: number, half: number, heelLift = 0): void => {
  const heelY = cy - heelLift;
  fill(grid, cx - half, heelY, cx - half + 2, heelY + 4, "b");
  fill(grid, cx - half + 2, cy, cx + half + 1, cy + 4, "b");
  fill(grid, cx - half + 1, heelY + 1, cx + half, cy + 3, "B");
  if (heelLift === 0) {
    fill(grid, cx - 2, cy + 5, cx + 3, cy + 6, "k");
  } else {
    fill(grid, cx + half - 4, cy + 5, cx + half + 1, cy + 6, "k");
  }
  fill(grid, cx + half - 1, cy + 2, cx + half + 2, cy + 4, "b");
  plot(grid, cx - 1, cy + 2, "B");
  plot(grid, cx + 1, cy + 2, "B");
};

const torso = (
  grid: Grid,
  cx: number,
  top: number,
  build: Build,
  lean: number,
  character: Character,
): { readonly shirtBottom: number; readonly hipY: number; readonly mid: number } => {
  const neckY = top + build.headRy * 2 - 2 + build.hunch;
  const shirtBottom = neckY + build.chest;
  const hipY =
    shirtBottom + Math.max(8, Math.round(build.hip * 0.75)) + (character === "pip" ? 3 : 0);
  let mid = cx + lean;
  for (let y = neckY; y < shirtBottom; y++) {
    const t = (y - neckY) / Math.max(1, build.chest);
    const neckHalf = Math.round(build.shoulder * 0.55);
    let half = Math.round(neckHalf + (build.shoulder - neckHalf) * Math.sin(t * Math.PI));
    if (t > 0.62) {
      const u = (t - 0.62) / 0.38;
      half = Math.round(half * (1 - u) + build.waist * u);
    }
    mid = cx + lean;
    if (character === "rusty") {
      if (t > 0.55) {
        half -= 2;
      }
    } else if (character === "pip") {
      half += Math.round(2 * Math.sin(t * Math.PI));
    } else if (character === "mina" && t > 0.25) {
      half -= 3;
    } else if (character === "dobbs") {
      if (t > 0.45) {
        half -= 3;
      }
      mid = cx + lean + 2;
    }
    const left = mid - half;
    const right = mid + half;
    for (let x = left; x <= right; x++) {
      plot(grid, x, y, shirtMark(x, left, right));
    }
  }
  for (let y = shirtBottom; y < hipY; y++) {
    const t = (y - shirtBottom) / Math.max(1, hipY - shirtBottom);
    let half = Math.round(build.waist + (build.hip - build.waist) * t);
    mid = cx + lean;
    if (character === "dobbs") {
      mid += 7 + Math.round(build.hunch * 0.7);
    }
    const left = mid - half;
    const right = mid + half;
    for (let x = left; x <= right; x++) {
      plot(grid, x, y, pantsMark(x, left, right));
    }
  }
  const pecY = neckY + Math.round(build.chest * 0.4);
  if (character === "rusty") {
    disk(grid, mid - 3, pecY, 3, 3, "f");
    disk(grid, mid + 3, pecY + 1, 3, 2, "e");
    fill(grid, mid - 3, shirtBottom - 1, mid + 4, shirtBottom + 1, "A");
  } else if (character === "mina") {
    disk(grid, mid - 2, pecY, 4, 3, "f");
    disk(grid, mid + 2, pecY + 1, 3, 3, "c");
    fill(grid, mid - 4, shirtBottom - 1, mid + 4, shirtBottom + 2, "a");
    fill(grid, mid - 3, shirtBottom, mid + 3, shirtBottom + 1, "A");
  } else if (character === "dobbs") {
    disk(grid, mid + 2, shirtBottom - 3, 6, 4, "e");
    disk(grid, mid - 4, pecY, 4, 3, "c");
    fill(grid, mid - 3, shirtBottom, mid + 3, shirtBottom, "k");
    fill(grid, mid + 8, pecY, mid + 9, shirtBottom, "k");
    fill(grid, mid - 6, hipY - 2, mid - 5, hipY + 4, "k");
    fill(grid, mid + 6, shirtBottom - 4, mid + 8, shirtBottom - 3, ".");
    fill(grid, mid + 4, hipY - 1, mid + 5, hipY + 6, "k");
  } else {
    disk(grid, mid, pecY + 2, 6, 5, "c");
    fill(grid, mid - 5, shirtBottom - 1, mid + 5, shirtBottom, "e");
  }
  fill(grid, mid - 4, neckY, mid + 4, neckY + 2, "d");
  plot(grid, mid - 4, neckY + 1, "s");
  plot(grid, mid + 3, neckY + 2, "C");
  plot(grid, mid - 2, neckY + Math.round(build.chest * 0.55), "e");
  plot(grid, mid + 3, neckY + Math.round(build.chest * 0.7), "C");
  fill(
    grid,
    mid - Math.max(4, build.waist - 1),
    shirtBottom - 1,
    mid + Math.max(4, build.waist),
    shirtBottom + 1,
    "A",
  );
  fill(grid, mid - 3, shirtBottom, mid + 3, shirtBottom, "Z");
  return { shirtBottom, hipY, mid };
};

const legs = (
  grid: Grid,
  cx: number,
  hipY: number,
  build: Build,
  leftFoot: number,
  rightFoot: number,
  squat: number,
  character: Character,
  leftKneeDrop = 0,
  rightKneeDrop = 0,
  leftHeel = 0,
  rightHeel = 0,
  leftKneeIn = 0,
  rightKneeIn = 0,
): void => {
  const ankle = Math.min(70, hipY + build.thigh - squat);
  const knee = hipY + Math.round((ankle - hipY) * (character === "mina" ? 0.5 : 0.42));
  const leftKneeY = knee + leftKneeDrop;
  const rightKneeY = knee + rightKneeDrop;
  const thick = Math.max(4, Math.round(build.hip / 3.2));
  const leftHip = character === "dobbs" ? cx - 4 : cx - 6;
  const rightHip = character === "dobbs" ? cx + 8 : cx + 6;
  const leftKneeX = Math.round((leftHip + leftFoot) / 2) + leftKneeIn;
  const rightKneeX = Math.round((rightHip + rightFoot) / 2) + rightKneeIn;
  limb(grid, leftHip, hipY, leftKneeX, leftKneeY, Math.min(4, thick), "p");
  limb(grid, leftKneeX, leftKneeY, leftFoot, ankle - 1, 2, "q");
  limb(grid, rightHip, hipY, rightKneeX, rightKneeY, Math.min(4, thick), "q");
  limb(grid, rightKneeX, rightKneeY, rightFoot, ankle - 1, 2, "P");
  joint(grid, leftKneeX, leftKneeY, "P");
  joint(grid, rightKneeX, rightKneeY, "P");
  fill(grid, leftKneeX - 1, leftKneeY - 1, leftKneeX + 1, leftKneeY + 1, "k");
  fill(grid, rightKneeX - 1, rightKneeY - 1, rightKneeX + 1, rightKneeY + 1, "k");
  if (character === "mina") {
    line(grid, leftKneeX - 1, hipY + 2, leftKneeX - 1, knee - 2, "P", 1);
    line(grid, rightKneeX + 1, hipY + 2, rightKneeX + 1, knee - 2, "p", 1);
  }
  boot(grid, leftFoot, ankle, build.boot, leftHeel);
  boot(grid, rightFoot, ankle, build.boot, rightHeel);
  if (character === "rusty") {
    plot(grid, leftKneeX - 3, leftKneeY, ".");
    plot(grid, leftKneeX - 2, leftKneeY, ".");
    plot(grid, rightKneeX + 2, rightKneeY, ".");
    plot(grid, rightKneeX + 3, rightKneeY, ".");
    fill(grid, leftFoot - 2, ankle, leftFoot + 3, ankle, "k");
    fill(grid, rightFoot - 2, ankle, rightFoot + 3, ankle, "k");
    if (leftHeel > 0) {
      fill(grid, leftFoot - build.boot, ankle - leftHeel, leftFoot - build.boot + 2, ankle, ".");
    }
    if (rightHeel > 0) {
      fill(grid, rightFoot - build.boot, ankle - rightHeel, rightFoot - build.boot + 2, ankle, ".");
    }
  }
};

const chair = (
  grid: Grid,
  seatX: number,
  seatY: number,
  turn = 0,
  nudgeX = 0,
  nudgeY = 0,
): void => {
  const keep = (mark: string): boolean =>
    mark === "w" ||
    mark === "W" ||
    mark === "M" ||
    mark === "m" ||
    mark === "n" ||
    mark === "N" ||
    mark === "s" ||
    mark === "d" ||
    mark === "S" ||
    mark === "f" ||
    mark === "c" ||
    mark === "e" ||
    mark === "C";
  const drop = (mark: string): boolean =>
    mark === "p" ||
    mark === "q" ||
    mark === "P" ||
    mark === "b" ||
    mark === "B" ||
    mark === "k" ||
    mark === "A" ||
    mark === "a" ||
    mark === "Z";
  for (let y = 43; y <= 72; y++) {
    for (let x = 7; x <= 66; x++) {
      if (!drop(grid[y][x]) || keep(grid[y][x])) {
        continue;
      }
      plot(grid, x, y, ".");
    }
  }
  const paint = (x0: number, y0: number, x1: number, y1: number, mark: string): void => {
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (x < 0 || y < 0 || x >= SIZE || y >= SIZE || keep(grid[y][x])) {
          continue;
        }
        plot(grid, x, y, mark);
      }
    }
  };
  const sx = seatX + nudgeX;
  const sy = seatY + nudgeY;
  paint(sx - 14, sy - 12, sx - 11, sy + 2, "P");
  paint(sx - 14, sy - 12, sx - 11, sy - 11, "A");
  paint(sx - 13, sy - 10, sx - 12, sy, "q");
  paint(sx - 6, sy, sx + 16, sy + 3, "p");
  fill(grid, sx + 2, sy, sx + 14, sy, "A");
  paint(sx - 4, sy + 1, sx + 14, sy + 2, "q");
  const rearX = seatX - 8;
  const frontX = seatX + 10;
  const axleY = seatY + 12;
  for (let y = axleY - 7; y <= axleY + 7; y++) {
    for (let x = rearX - 7; x <= rearX + 7; x++) {
      const dx = (x - rearX) / 7;
      const dy = (y - axleY) / 7;
      if (dx * dx + dy * dy > 1 || keep(grid[y]?.[x] ?? "w")) {
        continue;
      }
      plot(grid, x, y, "q");
    }
  }
  for (let y = axleY - 4; y <= axleY + 4; y++) {
    for (let x = rearX - 4; x <= rearX + 4; x++) {
      const dx = (x - rearX) / 4;
      const dy = (y - axleY) / 4;
      if (dx * dx + dy * dy > 1 || keep(grid[y]?.[x] ?? "w")) {
        continue;
      }
      plot(grid, x, y, ".");
    }
  }
  for (let y = axleY - 6; y <= axleY + 6; y++) {
    for (let x = frontX - 6; x <= frontX + 6; x++) {
      const dx = (x - frontX) / 6;
      const dy = (y - axleY) / 6;
      if (dx * dx + dy * dy > 1 || keep(grid[y]?.[x] ?? "w")) {
        continue;
      }
      plot(grid, x, y, "q");
    }
  }
  for (let y = axleY - 3; y <= axleY + 3; y++) {
    for (let x = frontX - 3; x <= frontX + 3; x++) {
      const dx = (x - frontX) / 3;
      const dy = (y - axleY) / 3;
      if (dx * dx + dy * dy > 1 || keep(grid[y]?.[x] ?? "w")) {
        continue;
      }
      plot(grid, x, y, ".");
    }
  }
  paint(rearX, axleY - 1, frontX, axleY, "k");
  paint(seatX + 1, seatY + 4, seatX + 4, seatY + 15, "p");
  paint(seatX + 6, seatY + 4, seatX + 9, seatY + 15, "q");
  fill(grid, rearX - 1, axleY - 1, rearX + 1, axleY + 1, "A");
  fill(grid, frontX - 1, axleY - 1, frontX + 1, axleY + 1, "A");
  fill(grid, seatX + 6, seatY + 15, seatX + 16, seatY + 16, "k");
  fill(grid, seatX + 4, seatY + 2, seatX + 6, seatY + 3, "k");
  const spokes: ReadonlyArray<readonly [number, number]> =
    turn % 2 === 0
      ? [
          [0, -6],
          [0, 6],
          [-6, 0],
          [6, 0],
        ]
      : [
          [-4, -4],
          [4, 4],
          [-4, 4],
          [4, -4],
        ];
  for (const hubX of [rearX, frontX]) {
    for (const [dx, dy] of spokes) {
      const steps = Math.max(Math.abs(dx), Math.abs(dy), 1);
      for (let i = 2; i <= steps; i++) {
        const x = hubX + Math.round((dx * i) / steps);
        const y = axleY + Math.round((dy * i) / steps);
        if (x < 0 || y < 0 || x >= SIZE || y >= SIZE) {
          continue;
        }
        if (keep(grid[y][x]) || grid[y][x] === "A") {
          continue;
        }
        plot(grid, x, y, "k");
      }
    }
  }
};

const neck = (grid: Grid, cx: number, y: number, thick: number): void => {
  fill(grid, cx - thick, y, cx + thick, y + 4, "d");
  plot(grid, cx - thick, y + 1, "s");
  plot(grid, cx + thick, y + 2, "S");
};

const eye = (grid: Grid, x: number, y: number): void => {
  fill(grid, x, y, x + 1, y + 1, "t");
  plot(grid, x + 1, y + 1, "k");
};

const features = (grid: Grid, cx: number, cy: number, character: Character): void => {
  fill(grid, cx - 4, cy - 4, cx + 3, cy - 3, "d");
  eye(grid, cx - 4, cy - 1);
  eye(grid, cx + 2, cy);
  fill(grid, cx - 1, cy, cx, cy + 2, "S");
  plot(grid, cx, cy + 1, "d");
  if (character === "rusty") {
    fill(grid, cx - 5, cy + 3, cx + 4, cy + 5, "S");
    fill(grid, cx - 3, cy + 4, cx + 2, cy + 5, "H");
    fill(grid, cx - 2, cy + 3, cx + 2, cy + 5, "k");
    fill(grid, cx - 4, cy - 2, cx - 3, cy - 1, "H");
    fill(grid, cx + 2, cy - 2, cx + 3, cy - 1, "H");
    fill(grid, cx - 3, cy + 8, cx + 3, cy + 9, ".");
    return;
  }
  if (character === "mina") {
    fill(grid, cx - 2, cy + 3, cx + 1, cy + 4, "S");
    fill(grid, cx - 4, cy - 3, cx + 3, cy - 2, "h");
    return;
  }
  if (character === "dobbs") {
    fill(grid, cx - 4, cy + 2, cx + 3, cy + 4, "S");
    fill(grid, cx - 2, cy + 3, cx + 2, cy + 4, "d");
    return;
  }
  fill(grid, cx - 2, cy + 3, cx + 2, cy + 4, "s");
  plot(grid, cx, cy + 4, "S");
};

const face = (
  grid: Grid,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  character: Character,
): void => {
  disk(grid, cx, cy, rx, ry, "d");
  disk(grid, cx - 2, cy - 1, Math.max(4, rx - 3), Math.max(4, ry - 3), "s");
  disk(grid, cx, cy + Math.round(ry * 0.45), Math.max(3, rx - 4), Math.max(2, ry - 6), "S");
  features(grid, cx, cy, character);
};

const headFront = (grid: Grid, cx: number, top: number, character: Character): void => {
  const build = BUILD[character];
  const hx = cx + build.headShift;
  const cy = top + build.headRy;
  neck(grid, hx, top + build.headRy * 2 - 2, character === "dobbs" ? 5 : 3);
  face(grid, hx, cy, build.headRx, build.headRy, character);
  if (character === "rusty") {
    fill(grid, cx - 8, top + 6, cx + 8, top + 11, "h");
    fill(grid, cx - 6, top + 4, cx + 6, top + 8, "i");
    const spikes: ReadonlyArray<readonly [number, number]> = [
      [-10, 7],
      [-3, 8],
      [3, 7],
      [9, 6],
    ];
    for (const [dx, height] of spikes) {
      line(grid, cx + dx, top + 6, cx + dx + 1, top + 6 - 2, "h", 2);
      line(grid, cx + dx + 1, top + 6 - 2, cx + dx, top + 6 - height, "H", 2);
      plot(grid, cx + dx, top + 5 - height, "i");
      plot(grid, cx + dx + 1, top + 6 - height, "h");
    }
    eye(grid, hx - 4, cy - 1);
    eye(grid, hx + 2, cy);
    return;
  }
  if (character === "mina") {
    disk(grid, cx, cy - 2, build.headRx + 3, build.headRy + 3, "h");
    disk(grid, cx - 2, cy - 4, build.headRx, build.headRy, "i");
    face(grid, hx, cy, build.headRx - 1, build.headRy - 1, character);
    eye(grid, hx - 4, cy - 1);
    eye(grid, hx + 2, cy);
    disk(grid, cx - 14, cy - 9, 4, 3, "Z");
    disk(grid, cx + 14, cy - 9, 4, 3, "Z");
    plot(grid, cx - 14, cy - 9, "a");
    plot(grid, cx + 14, cy - 9, "a");
    for (let i = 0; i < 20; i++) {
      const mark = i % 3 === 0 ? "h" : "H";
      const wx = i % 5 === 2 ? 1 : 0;
      plot(grid, cx - 16 - wx, top + 14 + i, mark);
      if (i % 4 !== 1) {
        plot(grid, cx - 15 - wx, top + 14 + i, mark);
      }
    }
    return;
  }
  if (character === "dobbs") {
    fill(grid, hx - 8, top + build.headRy * 2 - 2, hx + 8, top + build.headRy * 2 + 10, "d");
    fill(grid, hx - 4, top + build.headRy * 2 + 2, hx + 10, top + build.headRy * 2 + 12, "e");
    fill(grid, hx - 10, top + build.headRy * 2 + 4, hx + 2, top + build.headRy * 2 + 14, "C");
    fill(grid, cx - 11, top + 2, cx + 11, top + 8, "H");
    fill(grid, cx - 9, top + 3, cx + 8, top + 6, "h");
    fill(grid, hx - 3, cy - 1, hx + 4, cy, "l");
    plot(grid, hx - 3, cy - 1, "H");
    plot(grid, hx - 2, cy - 1, "H");
    plot(grid, hx + 3, cy - 1, "H");
    plot(grid, hx + 4, cy - 1, "H");
    plot(grid, hx, cy, "d");
    plot(grid, hx - 10, cy, "A");
    plot(grid, hx + 8, cy, "Z");
    plot(grid, hx - 10, cy + 1, ".");
    plot(grid, hx + 8, cy + 1, ".");
    fill(grid, hx - 8, top + 2, hx - 5, top + 4, "H");
    fill(grid, hx - 2, top + 2, hx + 2, top + 4, "h");
    fill(grid, hx + 5, top + 2, hx + 8, top + 4, "H");
    fill(grid, hx - 3, cy + 5, hx + 4, cy + 7, "S");
    fill(grid, hx - 2, cy + 8, hx + 2, cy + 12, "d");
    plot(grid, hx - 2, cy + 8, ".");
    plot(grid, hx + 2, cy + 8, ".");
    eye(grid, hx - 4, cy + 2);
    eye(grid, hx + 2, cy + 2);
    return;
  }
  disk(grid, hx, cy - 1, build.headRx, build.headRy, "h");
  disk(grid, hx - 2, cy - 3, 6, 5, "i");
  face(grid, hx, cy + 1, build.headRx - 1, build.headRy - 2, character);
  eye(grid, hx - 4, cy);
  eye(grid, hx + 2, cy + 1);
  disk(grid, hx - 14, cy + 6, 6, 4, "h");
  fill(grid, cx - 8, top + build.headRy * 2 + 1, cx + 8, top + build.headRy * 2 + 5, "a");
  fill(grid, cx - 6, top + build.headRy * 2 + 2, cx + 6, top + build.headRy * 2 + 4, "A");
};

const headSide = (grid: Grid, cx: number, top: number, character: Character): void => {
  const build = BUILD[character];
  const cy = top + build.headRy;
  neck(grid, cx + build.headShift, top + build.headRy * 2 - 2, character === "dobbs" ? 5 : 3);
  face(grid, cx + 2 + build.headShift, cy, build.headRx - 1, build.headRy, character);
  if (character === "rusty") {
    line(grid, cx + 2, top + 4, cx + 3, top - 3, "h", 2);
    line(grid, cx + 8, top + 5, cx + 9, top - 2, "H", 2);
    line(grid, cx - 5, top + 6, cx - 4, top - 1, "h", 2);
    fill(grid, cx - 6, top + 5, cx + 10, top + 10, "h");
    return;
  }
  if (character === "mina") {
    disk(grid, cx + 2, cy - 2, build.headRx + 2, build.headRy + 2, "h");
    face(grid, cx + 2, cy, build.headRx - 2, build.headRy - 1, character);
    eye(grid, cx - 2, cy - 1);
    eye(grid, cx + 4, cy);
    disk(grid, cx + 13, top + 2, 5, 4, "Z");
    plot(grid, cx + 13, top + 2, "a");
    for (let i = 0; i < 18; i++) {
      const wx = i % 5 === 3 ? 1 : 0;
      plot(grid, cx - 11 - wx, top + 12 + i, i % 3 === 0 ? "h" : "H");
      if (i % 4 !== 2) {
        plot(grid, cx - 10 - wx, top + 12 + i, "H");
        plot(grid, cx - 10 - wx, top + 13 + i, "h");
      }
    }
    return;
  }
  if (character === "dobbs") {
    fill(grid, cx - 6, top + 2, cx + 13, top + 8, "H");
    fill(grid, cx - 1, cy - 1, cx + 6, cy, "l");
    plot(grid, cx - 1, cy - 1, "H");
    plot(grid, cx, cy - 1, "H");
    plot(grid, cx + 5, cy - 1, "H");
    plot(grid, cx + 6, cy - 1, "H");
    plot(grid, cx + 2, cy, "d");
    plot(grid, cx + 13, cy, "A");
    fill(grid, cx, cy + 6, cx + 4, cy + 11, "d");
    eye(grid, cx - 2, cy + 2);
    eye(grid, cx + 6, cy + 2);
    return;
  }
  disk(grid, cx + 2, cy, build.headRx - 1, build.headRy - 1, "h");
  face(grid, cx + 3, cy + 1, build.headRx - 2, build.headRy - 2, character);
  eye(grid, cx - 1, cy);
  eye(grid, cx + 5, cy + 1);
  disk(grid, cx - 13, cy + 6, 6, 4, "h");
  fill(grid, cx - 4, top + build.headRy * 2, cx + 8, top + build.headRy * 2 + 4, "a");
};

const PLANT = 51;

const frames = (character: Character): Frames => {
  const look = LOOKS[character];
  const build = BUILD[character];
  const sprite = (rows: ReadonlyArray<string>): Sprite => ({ rows, palette: palette(look) });
  const top = 8 + build.rise;
  const shoulderY = (drop: number) => top + drop + build.headRy * 2 + build.hunch + 4;
  const plantPole = (grid: Grid, skipPalm: boolean): void => {
    const poleTop = character === "pip" ? 29 : 20;
    for (let y = poleTop; y < 68; y++) {
      if (skipPalm && y >= 43 && y <= 47) {
        continue;
      }
      let lean = 0;
      if (character === "pip") {
        if (y < 36) {
          lean = -1;
        }
      } else if (y < 32) {
        lean = -2;
      } else if (y < 36) {
        lean = -1;
      }
      plot(grid, PLANT + lean, y, "w");
      plot(grid, PLANT + 1 + lean, y, "W");
    }
  };
  const finishTool = (grid: Grid): void => {
    inkEdge(grid);
    joinTool(grid);
  };
  const cutJoints = (grid: Grid): void => {
    if (character === "rusty") {
      fill(grid, 36, 32, 39, 36, ".");
      fill(grid, 36, 33, 37, 35, "k");
    } else if (character === "mina") {
      for (let y = 18; y < 40; y++) {
        for (let x = 8; x < 24; x++) {
          if (grid[y][x] === "s" || grid[y][x] === "d") {
            plot(grid, x, y, ".");
            plot(grid, x + 1, y, "s");
            return;
          }
        }
      }
    } else if (character === "dobbs") {
      fill(grid, 40, 32, 41, 54, "k");
    } else {
      fill(grid, 40, 20, 42, 22, ".");
      fill(grid, 36, 36, 37, 37, "k");
    }
  };
  const wait = (shift: number): ReadonlyArray<string> => {
    const grid = sheet();
    const leanOut = character === "dobbs" || character === "pip" ? 1 : 0;
    const sink = 0;
    const cx = 25 + leanOut;
    const leftFoot = (character === "pip" ? 21 : 15) - (leanOut > 0 ? 2 : 0);
    const rightFoot = character === "pip" ? 35 : 31;
    const bodyX = cx - leanOut;
    const torsoTop = top + sink;
    const { hipY } = torso(grid, bodyX, torsoTop, build, 10, character);
    const kneeBend = sink > 0 ? 2 : 0;
    legs(
      grid,
      cx,
      hipY - sink,
      build,
      leftFoot,
      rightFoot,
      0,
      character,
      kneeBend,
      0,
      0,
      0,
      sink > 0 ? 5 : 0,
    );
    const headX = character === "rusty" ? bodyX + 5 : bodyX + 6;
    headFront(grid, headX, top + sink, character);
    plot(grid, bodyX, torsoTop + build.headRy * 2 - 2 + build.hunch, "c");
    plot(grid, bodyX + 1, torsoTop + build.headRy * 2 - 2 + build.hunch, "e");
    const sy = shoulderY(sink > 0 ? sink - 1 : 0);
    const minaElbow = character === "mina" ? 2 : 0;
    const shaftShoulder = character === "rusty" ? cx + build.shoulder + 7 : cx + build.shoulder + 5;
    const rearElbow = character === "dobbs" ? 40 : 42;
    const handY = 42 + sink;
    const handX = PLANT;
    const armThick = character === "dobbs" ? 5 : 4;
    arm(
      grid,
      shaftShoulder,
      sy + 1,
      40,
      38 + minaElbow + sink,
      handX,
      handY,
      armThick,
      true,
      character,
    );
    arm(
      grid,
      cx + 10,
      sy + 8,
      rearElbow,
      48 + minaElbow + sink,
      handX + 1,
      48 + sink,
      armThick,
      true,
      character,
    );
    if (character === "mina" || character === "pip") {
      fill(grid, 38, 37, 42, 42, "d");
      fill(grid, 43, 46, 46, 49, "d");
    }
    if (character === "mina") {
      fill(grid, 28, 22, 30, 28, "k");
    }
    if (character === "pip") {
      fill(grid, 36, 40, 42, 48 + sink, "d");
    }
    if (character === "dobbs") {
      fill(grid, cx + 8, sy, cx + 13, sy + 8, "c");
      fill(grid, 34, sy + 8, 39, sy + 16, "d");
      fill(grid, cx + 4, hipY - 1, cx + 8, hipY, "A");
      fill(grid, 40, 34, 41, 52, "k");
      plot(grid, 43, sy + 9, ".");
      plot(grid, 44, sy + 10, ".");
      plot(grid, 48, 36, "c");
      plot(grid, 48, 40, "e");
    }
    plantPole(grid, false);
    const cGrip = (y0: number): void => {
      fill(grid, 49, y0, 50, y0 + 4, "s");
      fill(grid, 53, y0, 54, y0 + 4, "s");
      fill(grid, 49, y0, 54, y0, "d");
      plot(grid, 48, y0 + 1, "d");
      plot(grid, 55, y0 + 1, "S");
      plot(grid, 49, y0 + 1, "d");
      plot(grid, 54, y0 + 1, "S");
      plot(grid, PLANT, y0, "w");
      plot(grid, PLANT + 1, y0, "W");
      plot(grid, PLANT, y0 + 1, "w");
      plot(grid, PLANT + 1, y0 + 1, "W");
    };
    cGrip(42 + sink);
    cGrip(48 + sink);
    for (let y = 46; y <= 47; y++) {
      plot(grid, 49, y, ".");
      plot(grid, 50, y, ".");
      plot(grid, 53, y, ".");
      plot(grid, 54, y, ".");
      plot(grid, PLANT, y, "w");
      plot(grid, PLANT + 1, y, "W");
    }
    stamp(grid, BLADE_DOWN, PLANT - 7, 64);
    ferrule(grid, PLANT - 1, 62);
    plantPole(grid, false);
    finishTool(grid);
    plantPole(grid, false);
    plot(grid, PLANT, handY, "w");
    plot(grid, PLANT + 1, handY, "W");
    plot(grid, PLANT, 48 + sink, "w");
    plot(grid, PLANT + 1, 48 + sink, "W");
    cutJoints(grid);
    stamp(grid, BLADE_DOWN, PLANT - 7, 64);
    fill(grid, PLANT - 5, 75, PLANT + 6, 76, "n");
    if (character === "rusty") {
      fill(grid, 27, 23, 33, 23, "s");
      fill(grid, 29, 24, 31, 25, "k");
      fill(grid, 28, 28, 32, 29, "s");
      plot(grid, 28, 24, "s");
      plot(grid, 32, 24, "s");
      fill(grid, 29, 23, 31, 23, "s");
    }
    if (character === "mina") {
      for (let y = 22; y <= 41; y++) {
        fill(grid, 14, y, 16, y, ".");
      }
      const locks: ReadonlyArray<readonly [number, number]> = [
        [14, 22],
        [14, 26],
        [15, 30],
        [14, 34],
        [15, 37],
      ];
      for (const [lx, ly] of locks) {
        fill(grid, lx, ly, lx + 1, ly + 2, "h");
      }
      fill(grid, 15, 39, 16, 40, "H");
      fill(grid, 27, 22, 35, 22, "s");
      fill(grid, 30, 22, 31, 22, "k");
      fill(grid, 29, 23, 32, 24, "s");
      fill(grid, 29, 25, 33, 27, "s");
      fill(grid, 29, 29, 33, 31, "s");
      let rightEye = 0;
      let rightEyeY = 0;
      for (let y = 16; y <= 28; y++) {
        for (let x = 20; x <= 40; x++) {
          if (grid[y][x] !== "t") {
            continue;
          }
          if (x >= rightEye) {
            rightEye = x;
            rightEyeY = y;
          }
        }
      }
      if (rightEye > 0) {
        const moved: Array<readonly [number, number, string]> = [];
        for (let y = rightEyeY - 1; y <= rightEyeY + 1; y++) {
          for (let x = rightEye - 1; x <= rightEye; x++) {
            const mark = grid[y]?.[x];
            if (mark !== "t" && mark !== "k") {
              continue;
            }
            moved.push([x, y, mark]);
          }
        }
        for (const [x, y] of moved) {
          plot(grid, x, y, "s");
        }
        for (const [x, y, mark] of moved) {
          plot(grid, x + 1, y, mark);
        }
      }
    }
    if (character === "dobbs") {
      fill(grid, 20, 11, 43, 18, ".");
      fillPoly(
        grid,
        [
          [20, 14],
          [24, 11],
          [39, 11],
          [43, 14],
          [41, 18],
          [23, 18],
        ],
        "H",
      );
      fill(grid, 34, 19, 38, 19, "l");
      fill(grid, 34, 20, 41, 20, "l");
      fill(grid, 24, 11, 26, 12, "h");
      plot(grid, 24, 11, "H");
      plot(grid, 39, 11, "H");
    }
    plot(grid, 50, 46, ".");
    plot(grid, 50, 47, ".");
    plot(grid, 53, 46, ".");
    plot(grid, 53, 47, ".");
    if (character === "pip") {
      fill(grid, 19, 25, 23, 30, ".");
      fill(grid, 39, 26, 43, 31, ".");
      fill(grid, 23, 27, 25, 29, "h");
      fill(grid, 31, 24, 34, 26, "i");
      fill(grid, 39, 30, 41, 32, "h");
      fill(grid, 17, 42, 22, 44, "h");
      fill(grid, 11, 46, 12, 47, "H");
      fill(grid, 29, 42, 33, 47, ".");
      fill(grid, 27, 45, 28, 46, ".");
      fill(grid, 34, 45, 35, 46, ".");
      fill(grid, 30, 42, 32, 42, "s");
      for (let y = 8; y <= 39; y++) {
        for (let x = 12; x <= 51; x++) {
          if (grid[y][x] !== "h" && grid[y][x] !== "i" && grid[y][x] !== "H") {
            continue;
          }
          if (x >= 22 && x <= 40 && y >= 20 && y <= 34) {
            continue;
          }
          if ((x + y) % 3 !== 0) {
            plot(grid, x, y, ".");
          }
        }
      }
      fill(grid, 30, 36, 34, 40, "s");
      fill(grid, 31, 38, 33, 41, "d");
      fill(grid, 28, 32, 29, 33, "t");
      fill(grid, 36, 33, 37, 34, "t");
      for (let y = 8; y <= 39; y++) {
        for (let x = 12; x <= 51; x++) {
          if (grid[y][x] !== "h" && grid[y][x] !== "i" && grid[y][x] !== "H") {
            continue;
          }
          if (x >= 22 && x <= 40 && y >= 12 && y <= 28) {
            continue;
          }
          plot(grid, x, y, ".");
        }
      }
      fill(grid, 28, 28, 36, 38, "s");
      fill(grid, 30, 32, 34, 36, "d");
      fill(grid, 28, 32, 29, 33, "t");
      fill(grid, 36, 33, 37, 34, "t");
      for (let y = 34; y <= 47; y++) {
        for (let x = 7; x <= 50; x++) {
          const mark = grid[y][x];
          const hair = mark === "h" || mark === "i" || mark === "H";
          const skin = mark === "s" || mark === "d" || mark === "S";
          if (y >= 38 && x <= 22 && hair) {
            plot(grid, x, y, ".");
            continue;
          }
          if (y >= 36 && y <= 46 && skin && x < 27) {
            plot(grid, x, y, ".");
          }
        }
      }
      fill(grid, 28, 34, 36, 36, "s");
      fill(grid, 30, 35, 34, 36, "S");
      fill(grid, 29, 36, 35, 46, "s");
      fill(grid, 30, 38, 34, 41, "d");
      fill(grid, 30, 43, 34, 45, "d");
      fill(grid, 30, 42, 32, 42, "s");
      for (let y = 36; y <= 46; y++) {
        for (let x = 18; x <= 39; x++) {
          if (x >= 29 && x <= 35) {
            continue;
          }
          const skin = grid[y][x] === "s" || grid[y][x] === "d" || grid[y][x] === "S";
          if (!skin) {
            continue;
          }
          if (y >= 42 || x < 28 || x > 36) {
            plot(grid, x, y, ".");
          }
        }
      }
      fill(grid, 29, 36, 35, 41, "s");
      fill(grid, 30, 40, 34, 41, "d");
      fill(grid, 30, 42, 32, 42, "s");
      fill(grid, 28, 30, 37, 31, "s");
      fill(grid, 30, 30, 34, 30, "d");
      fill(grid, 28, 32, 29, 33, "t");
      fill(grid, 36, 33, 37, 34, "t");
      fill(grid, 29, 34, 35, 35, "s");
      fill(grid, 29, 36, 35, 36, "s");
      for (let y = 36; y <= 41; y++) {
        for (let x = 28; x <= 37; x++) {
          if (grid[y][x] === "S" || grid[y][x] === "k") {
            plot(grid, x, y, "s");
          }
        }
      }
      plot(grid, 31, 38, "S");
      plot(grid, 32, 38, "k");
      plot(grid, 33, 39, "S");
    }
    if (character === "dobbs") {
      plot(grid, 48, 36, "c");
      plot(grid, 48, 40, "e");
      fill(grid, 39, 32, 41, 54, "k");
      fill(grid, 36, 36, 37, 50, "k");
      fill(grid, 45, 28, 46, 40, ".");
      fill(grid, 34, 42, 37, 56, ".");
      fill(grid, 42, 44, 44, 54, ".");
      plot(grid, 38, 42, "k");
      plot(grid, 39, 42, "s");
      plot(grid, 38, 43, "s");
      plot(grid, 35, 40, "s");
      plot(grid, 35, 36, "c");
      plot(grid, 36, 36, ".");
      plot(grid, 38, 44, ".");
      plot(grid, 38, 45, ".");
      fill(grid, 24, 11, 26, 11, "H");
      fill(grid, 23, 12, 27, 12, "H");
      fill(grid, 22, 13, 28, 13, "H");
      fill(grid, 23, 14, 28, 14, "h");
      plot(grid, 24, 11, "H");
      plot(grid, 39, 11, "H");
      fill(grid, 34, 19, 41, 20, "H");
      fill(grid, 34, 19, 35, 19, "l");
      fill(grid, 34, 20, 35, 20, "l");
      fill(grid, 40, 20, 41, 20, "l");
      fill(grid, 18, 62, 19, 72, ".");
      fill(grid, 30, 58, 31, 70, ".");
      for (let y = 44; y <= 51; y++) {
        for (let x = 54; x <= 59; x++) {
          if (grid[y][x] === "w" || grid[y][x] === "W") {
            continue;
          }
          plot(grid, x, y, ".");
        }
      }
    }
    for (let y = 36; y <= 50; y++) {
      for (let x = 36; x <= 46; x++) {
        const shirt = grid[y][x] === "f" || grid[y][x] === "c" || grid[y][x] === "e";
        const sleeve =
          grid[y][x + 1] === "s" ||
          grid[y][x + 1] === "d" ||
          grid[y][x - 1] === "s" ||
          grid[y][x - 1] === "d";
        if (shirt && sleeve) {
          plot(grid, x, y, "k");
        }
      }
    }
    if (shift > 0) {
      const dropSrc = freeze(grid);
      let firstShirt = SIZE;
      for (let y = 0; y <= 58; y++) {
        for (let x = 18; x <= 48; x++) {
          const mark = dropSrc[y][x];
          if (mark === "f" || mark === "c" || mark === "e" || mark === "C") {
            firstShirt = Math.min(firstShirt, y);
          }
        }
      }
      const lateShirt = firstShirt >= 47 && firstShirt < SIZE;
      const dropFrom = lateShirt ? firstShirt : Math.min(firstShirt, 28);
      const dropTo = lateShirt ? Math.min(SIZE - 2, firstShirt + 3) : Math.min(46, dropFrom + 14);
      for (let y = dropTo; y >= dropFrom; y--) {
        for (let x = 18; x <= 48; x++) {
          const mark = dropSrc[y][x];
          if (
            mark === "w" ||
            mark === "W" ||
            mark === "M" ||
            mark === "m" ||
            mark === "n" ||
            mark === "N" ||
            mark === "p" ||
            mark === "q" ||
            mark === "P" ||
            mark === "b" ||
            mark === "B"
          ) {
            continue;
          }
          plot(grid, x, y + 1, mark);
          if (y === dropFrom) {
            plot(grid, x, y, ".");
          }
        }
      }
      const elbow = dropSrc[48]?.[42];
      if (elbow !== undefined && elbow !== "." && elbow !== "w" && elbow !== "W") {
        plot(grid, 42, 49, elbow);
        plot(grid, 42, 48, ".");
      }
      fill(grid, 36, 38, 37, 40, "k");
      plot(grid, 38, 39, "s");
      plot(grid, 36, 41, "s");
    }
    if (character === "dobbs") {
      chair(grid, 36, 54);
      plantPole(grid, false);
      fill(grid, 39, 32, 41, 39, "k");
      plot(grid, 38, 42, "k");
      plot(grid, 39, 42, "s");
      plot(grid, 38, 43, "s");
      plot(grid, 48, 36, "c");
      plot(grid, 48, 40, "e");
    }
    return freeze(grid);
  };
  const ready = (drop: number): ReadonlyArray<string> => {
    const grid = sheet();
    const cx = drop > 0 ? 22 : 24;
    const hipDrop = 2 + (drop > 0 ? 1 : 0) + (character === "dobbs" ? 2 : 0);
    const { hipY } = torso(grid, cx, top + hipDrop, build, drop > 0 ? -6 : -4, character);
    let rear = drop > 0 ? cx - 28 : cx - 26;
    if (character === "rusty") {
      rear -= 3;
    }
    if (character === "mina") {
      rear -= 2;
    }
    legs(grid, cx, hipY, build, rear, cx + 18, hipDrop, character, 3, 0, drop > 0 ? 1 : 0, 0);
    boot(grid, rear, Math.min(70, hipY + build.thigh - hipDrop - 2), build.boot);
    headFront(grid, cx - 1, top + hipDrop, character);
    const sy = shoulderY(hipDrop);
    const holdX = drop > 0 ? 36 : 32;
    const highHand = drop > 0 ? 36 : 34;
    const lowHand = highHand + 8;
    const bladeX = drop > 0 ? 35 : 32;
    const bladeY = drop > 0 ? 49 : 47;
    if (drop > 0) {
      shaft(grid, holdX - 2, highHand - 6, holdX + 6, bladeY, 2);
    } else {
      shaft(grid, holdX, highHand - 6, holdX + 1, bladeY, 2);
    }
    const midX = Math.round((cx + build.shoulder - 1 + holdX) / 2);
    const midY = Math.round((sy + highHand) / 2);
    arm(grid, cx + build.shoulder - 1, sy, midX, midY, holdX, highHand, 4, true, character);
    arm(grid, cx - 6, sy + 4, midX - 8, midY + 4, holdX, lowHand, 4, true, character);
    if (character === "mina" || character === "pip") {
      fill(grid, cx + 8, sy + 6, cx + 12, sy + 10, "d");
    }
    if (character === "dobbs") {
      fill(grid, cx + 6, sy, cx + 12, sy + 8, "c");
      fill(grid, cx + 4, hipY - 1, cx + 8, hipY, "A");
      fill(grid, cx + 14, sy + 8, cx + 15, sy + 16, "k");
      fill(grid, 40, 34, 41, 50, "k");
      plot(grid, 48, 36, "c");
      plot(grid, 48, 40, "e");
    }
    if (character === "rusty") {
      fill(grid, cx + build.shoulder - 2, sy + 8, cx + build.shoulder + 4, sy + 9, ".");
    }
    if (character === "mina") {
      fill(grid, cx - 8, hipY + 10, cx - 6, hipY + 12, "P");
      fill(grid, cx + 8, hipY + 10, cx + 10, hipY + 12, "P");
    }
    fill(grid, midX, midY, midX + 1, midY + 1, "k");
    if (character === "mina") {
      plot(grid, midX - 1, midY, ".");
      fill(grid, holdX - 1, highHand - 1, holdX + 1, highHand + 1, "s");
      plot(grid, holdX, highHand, "w");
      plot(grid, holdX + 1, highHand, "W");
    }
    grip(grid, holdX, highHand);
    grip(grid, holdX, lowHand);
    for (let y = highHand; y <= lowHand; y++) {
      plot(grid, holdX, y, "w");
      plot(grid, holdX + 1, y, "W");
    }
    stamp(grid, BLADE_DOWN, bladeX, bladeY);
    ferrule(grid, holdX - 1, bladeY - 2);
    finishTool(grid);
    if (drop > 0) {
      shaft(grid, holdX - 2, bladeY - 6, holdX + 6, bladeY, 2);
    } else {
      shaft(grid, holdX, bladeY - 4, holdX + 1, bladeY, 2);
    }
    cutJoints(grid);
    stamp(grid, BLADE_DOWN, bladeX, bladeY);
    ferrule(grid, holdX - 1, bladeY - 2);
    let readyLift = 0;
    if (character === "dobbs") {
      readyLift = -3;
    } else if (character === "pip") {
      readyLift = -1;
    }
    const readySrc = freeze(grid);
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        plot(grid, x, y, ".");
      }
    }
    readySrc.forEach((row, y) => {
      for (let x = 0; x < row.length; x++) {
        plot(grid, x + 6, y + readyLift, row[x]);
      }
    });
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x <= 5; x++) {
        plot(grid, x, y, ".");
      }
    }
    for (let y = 77; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        plot(grid, x, y, ".");
      }
    }
    const bootXs: Array<number> = [];
    for (let y = 70; y <= 76; y++) {
      for (let x = 6; x < SIZE; x++) {
        if (grid[y][x] === "b" || grid[y][x] === "B") {
          bootXs.push(x);
        }
      }
    }
    if (bootXs.length > 0) {
      const left = Math.min(...bootXs);
      const right = Math.max(...bootXs);
      fill(grid, left, 75, Math.min(SIZE - 1, left + 8), 76, "B");
      if (right - left > 12) {
        fill(grid, Math.max(6, right - 8), 75, right, 76, "B");
      }
    } else {
      fill(grid, 18, 75, 25, 76, "B");
    }
    if (drop > 0) {
      stamp(grid, BLADE_DOWN, 41, 49);
      ferrule(grid, 42, 47);
      shaft(grid, 36, 28, 50, 52, 2);
    } else {
      stamp(grid, BLADE_DOWN, 38, 47);
      ferrule(grid, 43, 45);
      shaft(grid, 44, 28, 45, 49, 2);
    }
    const gripX = drop > 0 ? 42 : 44;
    grip(grid, gripX, drop > 0 ? 36 : 34);
    grip(grid, gripX, drop > 0 ? 44 : 42);
    for (let y = 31; y <= 52; y++) {
      for (let x = 38; x <= 53; x++) {
        if (grid[y][x] !== "k") {
          continue;
        }
        const wood =
          grid[y][x - 1] === "w" ||
          grid[y][x + 1] === "W" ||
          grid[y][x] === "w" ||
          grid[y - 1]?.[x] === "w" ||
          grid[y + 1]?.[x] === "W";
        if (wood) {
          plot(grid, x, y, "s");
        }
      }
    }
    const highGrip = drop > 0 ? 36 : 34;
    const lowGrip = drop > 0 ? 44 : 42;
    for (let y = 24; y <= 52; y++) {
      for (let x = 35; x <= 59; x++) {
        const skin = grid[y][x] === "s" || grid[y][x] === "d" || grid[y][x] === "S";
        const knot = grid[y][x] === "k";
        if (!skin && !knot) {
          continue;
        }
        const nearGrip = Math.abs(y - highGrip) <= 3 || Math.abs(y - lowGrip) <= 3;
        if (nearGrip) {
          continue;
        }
        const wood =
          grid[y][x - 1] === "w" ||
          grid[y][x + 1] === "w" ||
          grid[y][x - 1] === "W" ||
          grid[y][x + 1] === "W";
        if (wood || knot) {
          plot(grid, x, y, ".");
        }
      }
    }
    if (character === "dobbs") {
      chair(grid, 36, 54);
    }
    if (character === "dobbs" && drop > 0) {
      shaft(grid, 36, 28, 50, 52, 2);
      for (let y = 28; y <= 52; y++) {
        const xs: Array<number> = [];
        for (let x = 38; x <= 54; x++) {
          if (grid[y][x] === "w" || grid[y][x] === "W") {
            xs.push(x);
          }
        }
        if (xs.length <= 2) {
          continue;
        }
        const keep0 = xs[Math.floor((xs.length - 1) / 2)];
        const keep1 = keep0 + 1;
        for (const x of xs) {
          if (x === keep0 || x === keep1) {
            continue;
          }
          plot(grid, x, y, ".");
        }
        plot(grid, keep0, y, "w");
        plot(grid, keep1, y, "W");
      }
      for (let y = 34; y <= 50; y++) {
        for (let x = 38; x <= 54; x++) {
          if (grid[y][x] !== "w" && grid[y][x] !== "W") {
            continue;
          }
          if (grid[y][x - 1] === "s" || grid[y][x - 1] === "d" || grid[y][x - 1] === "S") {
            plot(grid, x - 1, y, ".");
          }
          if (grid[y][x + 1] === "s" || grid[y][x + 1] === "d" || grid[y][x + 1] === "S") {
            plot(grid, x + 1, y, ".");
          }
          if (grid[y][x - 1] === "A" || grid[y][x - 1] === "k") {
            plot(grid, x - 1, y, ".");
          }
          if (grid[y][x + 1] === "A" || grid[y][x + 1] === "k") {
            plot(grid, x + 1, y, ".");
          }
        }
      }
      const gripYs: ReadonlyArray<number> = [36, 44];
      for (const gy of gripYs) {
        let shaftX = -1;
        for (let x = 38; x <= 54; x++) {
          if (grid[gy][x] === "w" || grid[gy][x] === "W") {
            shaftX = x;
            break;
          }
        }
        if (shaftX < 0) {
          continue;
        }
        plot(grid, shaftX - 1, gy, ".");
        plot(grid, shaftX - 2, gy, "s");
        plot(grid, shaftX - 2, gy + 1, "d");
        let east = shaftX;
        while (east < 54 && (grid[gy][east] === "w" || grid[gy][east] === "W")) {
          east += 1;
        }
        plot(grid, east, gy, "s");
        plot(grid, east, gy + 1, "d");
      }
      plot(grid, 43, 40, ".");
      plot(grid, 44, 41, ".");
    }
    if (character === "pip" && drop > 0) {
      for (let y = 14; y <= 49; y++) {
        for (let x = 43; x <= 59; x++) {
          const hair = grid[y][x] === "h" || grid[y][x] === "i" || grid[y][x] === "H";
          if (!hair) {
            continue;
          }
          if (
            grid[y][x - 1] === "w" ||
            grid[y][x + 1] === "w" ||
            grid[y][x - 1] === "W" ||
            grid[y][x + 1] === "W"
          ) {
            plot(grid, x, y, ".");
          }
        }
      }
      fill(grid, 40, 40, 42, 43, "s");
      plot(grid, 41, 41, "d");
      fill(grid, 46, 34, 48, 37, "s");
      plot(grid, 47, 35, "d");
      for (let y = 28; y <= 40; y++) {
        for (let x = 44; x <= 51; x++) {
          const hair = grid[y][x] === "h" || grid[y][x] === "i" || grid[y][x] === "H";
          if (hair) {
            plot(grid, x, y, ".");
          }
        }
        plot(grid, 46, y, "w");
        plot(grid, 47, y, "W");
      }
      for (let y = 14; y <= 49; y++) {
        for (let x = 28; x <= 45; x++) {
          const hair = grid[y][x] === "h" || grid[y][x] === "i" || grid[y][x] === "H";
          if (!hair) {
            continue;
          }
          if (grid[y][x + 1] === "w" || grid[y][x + 1] === "W") {
            plot(grid, x, y, ".");
            plot(grid, x + 1, y, "w");
          } else if (grid[y][x + 1] !== ".") {
            plot(grid, x + 1, y, ".");
          }
        }
      }
      for (let y = 41; y <= 49; y++) {
        for (let x = 43; x <= 53; x++) {
          if (grid[y][x] !== "w" && grid[y][x] !== "W") {
            continue;
          }
          if (grid[y][x - 1] === "s" || grid[y][x - 1] === "d") {
            plot(grid, x - 1, y, ".");
          }
          if (grid[y][x + 1] === "s" || grid[y][x + 1] === "d" || grid[y][x + 1] === "h") {
            plot(grid, x + 1, y, ".");
          }
        }
      }
      plot(grid, 44, 44, "s");
      plot(grid, 48, 42, "s");
    }
    return freeze(grid);
  };
  const walk = (
    pelvis: number,
    leftAbs: number,
    rightAbs: number,
    bob: number,
    _shovelX: number,
    _plant: boolean,
    _reach: boolean,
    leftHeel = 0,
    rightHeel = 0,
    lean = 2,
  ): ReadonlyArray<string> => {
    const grid = sheet();
    const cx = pelvis;
    const { hipY } = torso(grid, cx, top + bob, build, lean, character);
    let leftDrop = 0;
    let rightDrop = 0;
    let leftFoot = leftAbs;
    let rightFoot = rightAbs;
    if (_plant) {
      rightDrop = 0;
      leftDrop = 2;
    } else {
      leftDrop = 0;
      rightDrop = 2;
    }
    const kneeShift = character === "mina" || character === "pip" ? 3 : 0;
    legs(
      grid,
      cx,
      hipY,
      build,
      leftFoot,
      rightFoot,
      Math.max(0, bob),
      character,
      leftDrop,
      rightDrop,
      leftHeel,
      rightHeel,
      _plant ? -kneeShift : kneeShift,
      _plant ? kneeShift : -kneeShift,
    );
    const plantX = _plant ? rightAbs : leftAbs;
    boot(grid, plantX, 73, build.boot, 0);
    headSide(grid, cx + 2 + build.headShift + (_plant ? -2 : 2), top + bob, character);
    if (character === "pip" && !_plant) {
      fill(grid, cx + 10, top + bob + 8, cx + 11, top + bob + 16, "k");
    }
    const sy = shoulderY(-bob);
    const swing = _plant ? -1 : 1;
    let walkFrame = 0;
    if (!_plant && bob === -2) {
      walkFrame = 1;
    } else if (_plant && bob === 0) {
      walkFrame = 2;
    } else if (_plant && bob === -2) {
      walkFrame = 3;
    }
    const walkTops: Readonly<Record<Character, readonly [number, number, number, number]>> = {
      rusty: [59, 61, 59, 57],
      mina: [57, 59, 57, 55],
      dobbs: [59, 61, 59, 57],
      pip: [63, 65, 63, 61],
    };
    const bladeY = walkTops[character][walkFrame] - 1;
    const hold = _plant ? 50 : 48;
    const topSwing = [0, 2, -2, 0][walkFrame] ?? 0;
    const bladeLag = [0, -4, 2, -2][walkFrame] ?? 0;
    const topX = hold + topSwing;
    const lagX = hold + bladeLag;
    const rearHandX = cx - 8 + swing * 3;
    const rearHandY = sy + 22 - swing;
    arm(grid, cx - 8 - swing, sy + 2, cx - 12, sy + 10, rearHandX, rearHandY, 4, true, character);
    shaft(grid, hold, sy + 2, hold + 1, bladeY, 2);
    shaft(grid, topX, sy - 6, hold, sy + 2, 2);
    arm(
      grid,
      cx + build.shoulder - 2 + swing,
      sy,
      cx + 10,
      sy + 6,
      hold,
      sy + 4,
      4,
      true,
      character,
    );
    grip(grid, hold, sy + 4);
    plot(grid, topX - 2, sy, "s");
    plot(grid, topX - 1, sy, "W");
    stamp(grid, BLADE_DOWN, lagX - 5, bladeY);
    ferrule(grid, lagX - 1, bladeY - 2);
    shaft(grid, lagX, bladeY - 3, lagX + 1, bladeY, 2);
    finishTool(grid);
    cutJoints(grid);
    if (character === "pip" || character === "mina") {
      plot(grid, cx, top + bob + 20, "c");
    }
    if (character === "mina" || character === "pip") {
      fill(grid, cx + 8, sy + 8, cx + 10, sy + 9, "C");
      plot(grid, cx - 10, sy + 12, ".");
    }
    if (character === "pip") {
      arm(grid, cx - 10, sy + 4, cx - 14, sy + 12, cx - 16, sy + 18, 2, true, character);
    }
    if (character === "dobbs") {
      fill(grid, cx - 1, hipY, cx + 2, hipY + 3, "k");
    }
    plot(grid, hold - 2, 36 - bob, "s");
    plot(grid, hold - 1, 36 - bob, "W");
    plot(grid, hold, 36 - bob, "w");
    if (character === "mina" || character === "pip") {
      for (let y = 27; y <= 52; y++) {
        if (y === 36 - bob) {
          continue;
        }
        for (let x = 44; x <= 56; x++) {
          if (grid[y][x] !== "s" && grid[y][x] !== "d") {
            continue;
          }
          const west = grid[y][x - 1];
          const east = grid[y][x + 1];
          if (west === "w" || west === "W" || east === "w" || east === "W") {
            plot(grid, x, y, ".");
          }
        }
      }
    }
    plot(grid, hold - 2, 36 - bob, "s");
    plot(grid, hold - 1, 36 - bob, "W");
    plot(grid, hold, 36 - bob, "w");
    if (character === "mina" || character === "pip") {
      for (let y = 27; y < 36 - bob; y++) {
        for (let x = 44; x <= 56; x++) {
          if (grid[y][x] !== "s" && grid[y][x] !== "d") {
            continue;
          }
          const west = grid[y][x - 1];
          const east = grid[y][x + 1];
          if (west === "w" || west === "W" || east === "w" || east === "W") {
            plot(grid, x, y, ".");
          }
        }
      }
    }
    for (let y = 75; y <= 76; y++) {
      for (let x = 0; x < SIZE; x++) {
        if (grid[y][x] === "b" || grid[y][x] === "B") {
          plot(grid, x, y, ".");
        }
      }
    }
    let soleLeft = plantX - 3;
    let soleRight = plantX + 4;
    if (character === "rusty") {
      if (_plant) {
        soleLeft = 55;
        soleRight = 61;
      } else {
        soleLeft = 38;
        soleRight = 44;
      }
    }
    fill(grid, soleLeft, 75, Math.min(soleRight, soleLeft + 7), 76, "B");
    const passX = _plant ? leftAbs : rightAbs;
    fill(grid, passX - 4, 70, passX + 3, 72, "b");
    fill(grid, passX - 3, 71, passX + 2, 72, "B");
    if (character === "dobbs") {
      const walkSrc = freeze(grid);
      for (let y = 0; y < SIZE; y++) {
        for (let x = 0; x < SIZE; x++) {
          plot(grid, x, y, ".");
        }
      }
      walkSrc.forEach((row, y) => {
        for (let x = 0; x < row.length; x++) {
          plot(grid, x - 3, y, row[x]);
        }
      });
    }
    for (let y = 77; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        plot(grid, x, y, ".");
      }
    }
    const slide = character === "dobbs" ? -3 : 0;
    const tip = bladeY;
    stamp(grid, BLADE_DOWN, lagX - 5 + slide, tip);
    ferrule(grid, lagX - 1 + slide, tip - 2);
    shaft(grid, hold + slide, sy + 2, hold + 1 + slide, tip, 2);
    shaft(grid, topX + slide, sy - 6, hold + slide, sy + 2, 2);
    shaft(grid, lagX + slide, tip - 3, lagX + 1 + slide, tip, 2);
    joinTool(grid);
    for (let y = 77; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        plot(grid, x, y, ".");
      }
    }
    fill(grid, soleLeft + slide, 75, Math.min(soleRight, soleLeft + 7) + slide, 76, "B");
    fill(grid, passX - 4 + slide, 70, passX + 3 + slide, 72, "b");
    fill(grid, plantX - 3 + slide, 75, plantX + 3 + slide, 76, "B");
    if (character === "dobbs") {
      chair(grid, 36, 54, walkFrame);
    }
    if (character === "mina" || character === "pip") {
      plot(grid, cx, top + bob + 20, "c");
    }
    if (character === "mina" && walkFrame === 1) {
      for (let y = 12; y <= 28; y++) {
        for (let x = 52; x <= 58; x++) {
          if (grid[y][x] === "t") {
            plot(grid, x, y, "k");
          }
        }
      }
      const mouthSrc = freeze(grid);
      for (let y = 16; y <= 26; y++) {
        for (let x = 42; x <= 58; x++) {
          if (mouthSrc[y][x] !== "S") {
            continue;
          }
          plot(grid, x, y, mouthSrc[y][x - 1] === "S" ? "S" : "d");
          plot(grid, x - 1, y, "S");
        }
      }
    }
    if (character === "dobbs" && walkFrame === 1) {
      for (let y = 70; y <= 76; y++) {
        for (let x = 21; x <= 52; x++) {
          if (grid[y][x] !== "q" && grid[y][x] !== "p") {
            continue;
          }
          const open =
            grid[y][x - 1] === "." || grid[y][x + 1] === "." || (grid[y + 1]?.[x] ?? ".") === ".";
          if (open) {
            plot(grid, x, y, ".");
          }
        }
      }
      for (let y = 58; y <= 63; y++) {
        for (let x = 47; x <= 52; x++) {
          if (grid[y][x] === "q" || grid[y][x] === "k") {
            plot(grid, x, y, ".");
          }
        }
      }
      plot(grid, 47, 58, "q");
      plot(grid, 48, 58, "q");
      plot(grid, 49, 59, "q");
      plot(grid, 50, 60, "q");
      plot(grid, 51, 61, "q");
    }
    return freeze(grid);
  };
  const down = (
    bladeX: number,
    bladeY: number,
    squat: number,
    chips: boolean,
  ): ReadonlyArray<string> => {
    const grid = sheet();
    const pull = bladeY >= 56 ? 4 : 0;
    const cx = 30 + pull;
    const crush = Math.max(0, squat) + (pull > 0 ? 3 : 0);
    const { hipY } = torso(grid, cx, top + squat, build, squat < 0 ? 4 : 1, character);
    const stance = pull > 0 ? 15 : 12;
    legs(grid, cx, hipY, build, cx - stance, cx + stance, crush, character, pull > 0 ? 3 : 0);
    if (chips) {
      fill(grid, cx - stance, 75, cx - stance + 7, 76, "B");
      fill(grid, cx + stance - 7, 75, cx + stance, 76, "B");
    }
    headFront(grid, cx, top + squat, character);
    const sy = shoulderY(squat);
    const handX = bladeX + 5;
    const handY = bladeY < 40 ? 22 : bladeY - 16 + (pull > 0 ? 5 : 0);
    const midX = Math.round((handX + bladeX + 6) / 2);
    const midY = Math.round((sy + handY) / 2);
    arm(grid, cx + build.shoulder - 2, sy, midX, midY - 4, handX, handY, 4, true, character);
    arm(grid, cx - 2, sy, midX + 6, midY + 2, handX, handY + 8, 4, true, character);
    shaft(grid, handX, handY - 4, bladeX + 6, bladeY, 2);
    stamp(grid, BLADE_DOWN, bladeX, bladeY);
    ferrule(grid, bladeX + 5, bladeY - 2);
    grip(grid, handX, handY);
    grip(grid, handX, handY + 8);
    finishTool(grid);
    shaft(grid, handX, handY, bladeX + 6, bladeY, 2);
    grip(grid, handX, handY);
    grip(grid, handX, handY + 8);
    cutJoints(grid);
    stamp(grid, BLADE_DOWN, bladeX, bladeY);
    ferrule(grid, bladeX + 5, bladeY - 2);
    fill(
      grid,
      bladeX + 1,
      Math.min(SIZE - 2, bladeY + 6),
      bladeX + 10,
      Math.min(SIZE - 1, bladeY + 7),
      "n",
    );
    grip(grid, handX, handY);
    grip(grid, handX, handY + 9);
    for (let y = 0; y < SIZE; y++) {
      if (y === handY || y === handY + 9) {
        continue;
      }
      for (let x = 1; x < SIZE - 2; x++) {
        if (grid[y][x] !== "w" && grid[y][x] !== "W") {
          continue;
        }
        const leftSkin = grid[y][x - 1] === "s" || grid[y][x - 1] === "d" || grid[y][x - 1] === "S";
        const rightSkin =
          grid[y][x + 2] === "s" || grid[y][x + 2] === "d" || grid[y][x + 2] === "S";
        if (leftSkin && rightSkin) {
          plot(grid, x - 1, y, ".");
          plot(grid, x + 2, y, ".");
        }
      }
    }
    if (chips) {
      fill(grid, 40, 71, 41, 72, "Q");
      plot(grid, 41, 73, "D");
      fill(grid, 54, 75, 56, 76, "o");
      plot(grid, 53, 76, "Q");
      fill(grid, 44, 75, 52, 75, ".");
    }
    if (character === "rusty") {
      for (let y = 20; y <= 30; y++) {
        for (let x = 26; x <= 37; x++) {
          if (grid[y][x] === "k") {
            plot(grid, x, y, "s");
          }
        }
      }
      if (squat < 0) {
        fill(grid, 29, 24, 31, 25, "k");
      } else if (squat === 0) {
        plot(grid, 30, 26, "S");
        plot(grid, 31, 26, "S");
      } else if (squat >= 2) {
        fill(grid, 29, 26, 32, 26, "k");
      }
    }
    if (character === "pip" && bladeY < 40) {
      for (let y = 20; y <= 38; y++) {
        for (let x = 39; x <= 52; x++) {
          const mark = grid[y][x];
          if (mark !== "h" && mark !== "i" && mark !== "H") {
            continue;
          }
          const east = grid[y][x + 1];
          const south = grid[y + 1]?.[x];
          if (east === "w" || east === "W" || south === "w" || south === "W") {
            plot(grid, x, y, ".");
          }
        }
      }
    }
    if (character === "pip" && bladeY >= 50) {
      for (let x = 0; x < SIZE; x++) {
        const mark = grid[79][x];
        if (mark === "p" || mark === "q" || mark === "P" || mark === "b" || mark === "B") {
          plot(grid, x, 79, ".");
          if (grid[77][x] === ".") {
            plot(grid, x, 77, mark);
          }
        }
      }
    }
    if (character === "dobbs" && bladeY >= 64) {
      for (let x = 0; x < SIZE; x++) {
        const mark = grid[79][x];
        if (mark === "p" || mark === "q" || mark === "P" || mark === "b" || mark === "B") {
          plot(grid, x, 79, ".");
          if (grid[78][x] === ".") {
            plot(grid, x, 78, mark);
          }
        }
      }
    }
    if (character === "dobbs") {
      chair(grid, 36, 54);
    }
    return freeze(grid);
  };
  const left = (bladeX: number, bladeY: number, reach: number): ReadonlyArray<string> => {
    const grid = sheet();
    const cx = 40 - Math.min(4, reach);
    const bodyTop = reach === 0 ? top - 2 : top;
    const { hipY } = torso(grid, cx, bodyTop, build, -4 - reach, character);
    const lead = cx - 10 - Math.min(4, reach);
    const rear = 50 + (reach >= 2 ? 2 : 0);
    let squat = 0;
    if (reach > 3) {
      squat = 4;
    } else if (reach > 2) {
      squat = 2;
    }
    legs(grid, cx, hipY, build, lead, rear, squat, character);
    boot(grid, rear, 72, build.boot + 1);
    if (reach >= 3) {
      boot(grid, lead, 70, build.boot + 1);
      fill(grid, lead, 60, lead + 1, 62, "k");
    }
    headSide(grid, cx - 4, bodyTop + Math.min(4, reach), character);
    let socketX = bladeX + 6;
    const socketY = bladeY + 2;
    const sy = shoulderY(0);
    let elbowX = cx - 12;
    const rearElbowX = cx + 2;
    let frontShoulder = cx - build.shoulder + 2;
    if (reach >= 2) {
      frontShoulder -= 3;
    }
    if (reach >= 4) {
      frontShoulder -= 4;
    }
    const elbowY = sy + 22;
    if (character === "mina") {
      if (reach < 2) {
        frontShoulder += 3;
        elbowX += 2;
      } else {
        frontShoulder -= 2;
      }
    }
    let leadHandX = socketX;
    let leadHandY = socketY - 2;
    let rearHandX = socketX;
    let rearHandY = socketY + 5;
    if (reach >= 2 && reach < 3) {
      leadHandX = 32;
      leadHandY = 40;
      rearHandX = 44;
      rearHandY = 33;
    } else if (reach >= 3) {
      leadHandX = socketX - 1;
      leadHandY = socketY - 2;
      rearHandX = socketX + 5;
      rearHandY = socketY + 6;
    }
    arm(grid, frontShoulder, sy + 2, elbowX, elbowY, leadHandX, leadHandY, 4, true, character);
    arm(grid, cx + 4, sy + 10, rearElbowX + 2, sy + 16, rearHandX, rearHandY, 4, true, character);
    plot(grid, elbowX, elbowY, "k");
    plot(grid, elbowX + 1, elbowY, "s");
    plot(grid, elbowX, elbowY + 1, "s");
    plot(grid, rearElbowX, sy + 18, "k");
    plot(grid, rearElbowX + 1, sy + 18, "s");
    plot(grid, rearElbowX, sy + 19, "s");
    if (character === "dobbs") {
      fill(grid, cx + 6, sy + 6, cx + 7, sy + 16, "k");
      fill(grid, socketX - 3, socketY, socketX - 2, socketY + 4, "k");
    }
    if (reach >= 2 && reach < 3) {
      if (character === "pip") {
        shaft(grid, 44, 40, 11, 52, 2);
      } else {
        shaft(grid, 44, 33, 11, 52, 2);
      }
    } else {
      shaft(grid, rearHandX, rearHandY, leadHandX, leadHandY, 2);
      shaft(grid, leadHandX, leadHandY, socketX, socketY, 2);
    }
    stamp(grid, BLADE_LEFT, bladeX, bladeY);
    ferrule(grid, socketX - 1, socketY);
    grip(grid, leadHandX, leadHandY);
    grip(grid, rearHandX, rearHandY);
    plot(grid, leadHandX - 1, leadHandY, "k");
    plot(grid, leadHandX + 1, leadHandY, "k");
    plot(grid, leadHandX - 1, leadHandY + 1, "k");
    plot(grid, leadHandX + 1, leadHandY + 1, "k");
    plot(grid, rearHandX + 1, rearHandY, "k");
    plot(grid, rearHandX - 1, rearHandY, "k");
    plot(grid, rearHandX + 1, rearHandY + 1, "k");
    if (reach !== 2) {
      shaft(grid, socketX, socketY, socketX + 16, socketY - 6, 2);
    }
    finishTool(grid);
    shaft(grid, socketX, socketY, socketX + 1, socketY, 2);
    fill(grid, socketX - 1, socketY - 2, socketX, socketY + 6, "s");
    plot(grid, socketX, socketY, "w");
    plot(grid, socketX + 1, socketY, "n");
    plot(grid, socketX + 1, socketY + 1, "N");
    plot(grid, socketX + 1, socketY + 2, "m");
    cutJoints(grid);
    plot(grid, elbowX, elbowY, "k");
    plot(grid, elbowX + 1, elbowY, "s");
    plot(grid, elbowX, elbowY + 1, "s");
    plot(grid, rearElbowX, sy + 18, "k");
    plot(grid, rearElbowX + 1, sy + 18, "s");
    plot(grid, rearElbowX, sy + 19, "s");
    stamp(grid, BLADE_LEFT, bladeX, bladeY);
    plot(grid, socketX + 1, socketY, "n");
    plot(grid, socketX + 1, socketY + 1, "N");
    plot(grid, socketX + 1, socketY + 2, "m");
    if (reach >= 3) {
      plot(grid, bladeX, bladeY + 3, "a");
      plot(grid, bladeX + 1, bladeY + 5, "A");
      plot(grid, bladeX + 2, bladeY + 4, "Z");
    }
    if (bladeX <= 2) {
      for (let y = 56; y <= 66; y++) {
        for (let x = 2; x <= 5; x++) {
          if (
            grid[y][x] === "n" ||
            grid[y][x] === "m" ||
            grid[y][x] === "M" ||
            grid[y][x] === "N"
          ) {
            plot(grid, x, y, ".");
          }
        }
      }
      fill(grid, 4, 56, 5, 58, "n");
      fill(grid, 3, 59, 5, 61, "n");
      fill(grid, 2, 62, 4, 64, "m");
      plot(grid, 2, 63, "M");
      fill(grid, 3, 65, 5, 66, "n");
      fill(grid, 5, 60, 6, 64, "N");
      plot(grid, 5, 62, "n");
    }
    if (character === "dobbs") {
      if (reach === 2) {
        chair(grid, 36, 54, 0, 1, 0);
      } else if (reach >= 4) {
        chair(grid, 36, 54, 0, 0, 1);
      } else {
        chair(grid, 36, 54);
      }
    }
    const gripR = character === "mina" ? 1 : 2;
    const atGrip = (x: number, y: number): boolean =>
      (Math.abs(x - leadHandX) <= gripR && Math.abs(y - leadHandY) <= gripR) ||
      (Math.abs(x - rearHandX) <= gripR && Math.abs(y - rearHandY) <= gripR);
    for (let y = 28; y <= 54; y++) {
      for (let x = 8; x <= 56; x++) {
        const flesh = grid[y][x] === "s" || grid[y][x] === "d" || grid[y][x] === "S";
        if (!flesh || atGrip(x, y)) {
          continue;
        }
        const west = grid[y][x - 1];
        const east = grid[y][x + 1];
        const north = grid[y - 1]?.[x];
        const south = grid[y + 1]?.[x];
        const wood =
          west === "w" ||
          west === "W" ||
          east === "w" ||
          east === "W" ||
          north === "w" ||
          north === "W" ||
          south === "w" ||
          south === "W";
        if (wood) {
          plot(grid, x, y, ".");
        }
      }
    }
    const stampHand = (hx: number, hy: number): void => {
      for (let y = hy - 1; y <= hy + 1; y++) {
        for (let x = hx - 1; x <= hx + 1; x++) {
          const mark = grid[y]?.[x];
          if (
            mark === "w" ||
            mark === "W" ||
            mark === "M" ||
            mark === "m" ||
            mark === "n" ||
            mark === "N"
          ) {
            continue;
          }
          plot(grid, x, y, "s");
        }
      }
      const center = grid[hy]?.[hx];
      if (center !== "w" && center !== "W") {
        plot(grid, hx, hy, "d");
      }
    };
    stampHand(leadHandX, leadHandY);
    stampHand(rearHandX, rearHandY);
    plot(grid, socketX, socketY, "w");
    plot(grid, socketX + 1, socketY, "W");
    if (reach >= 2 && reach < 3) {
      for (let y = 33; y <= 52; y++) {
        const xs: Array<number> = [];
        for (let x = 11; x <= 44; x++) {
          if (grid[y][x] === "w" || grid[y][x] === "W") {
            xs.push(x);
          }
        }
        if (xs.length <= 2) {
          continue;
        }
        const keep0 = xs[Math.floor((xs.length - 1) / 2)];
        const keep1 = keep0 + 1;
        for (const x of xs) {
          if (x === keep0 || x === keep1 || atGrip(x, y)) {
            continue;
          }
          plot(grid, x, y, ".");
        }
        plot(grid, keep0, y, "w");
        plot(grid, keep1, y, "W");
      }
    }
    if (character === "pip" && reach >= 1 && reach < 3) {
      for (let y = 22; y <= 39; y++) {
        for (let x = 24; x <= 42; x++) {
          if (grid[y][x] === "w" || grid[y][x] === "W") {
            plot(grid, x, y, ".");
          }
        }
      }
      for (let y = 22; y <= 39; y++) {
        for (let x = 24; x <= 42; x++) {
          if (grid[y][x] !== "t") {
            continue;
          }
          if (grid[y][x - 1] !== "t") {
            plot(grid, x - 1, y, "k");
          }
        }
      }
      let stamped = false;
      for (let y = 22; y <= 39 && !stamped; y++) {
        for (let x = 24; x <= 42 && !stamped; x++) {
          if (grid[y][x] === "t") {
            stamped = true;
          }
        }
      }
      if (!stamped) {
        fill(grid, 29, 30, 30, 31, "t");
        fill(grid, 36, 31, 37, 32, "t");
        plot(grid, 28, 30, "k");
        plot(grid, 28, 31, "k");
      }
      for (let y = 22; y <= 39; y++) {
        for (let x = 24; x <= 42; x++) {
          if (grid[y][x] !== "t") {
            continue;
          }
          if (grid[y][x - 1] !== "t") {
            plot(grid, x - 1, y, "k");
          }
        }
      }
      if (reach < 2) {
        let beardY = -1;
        for (let y = 39; y >= 22; y--) {
          for (let x = 24; x <= 42; x++) {
            if (grid[y][x] === "h" || grid[y][x] === "H" || grid[y][x] === "a") {
              beardY = y;
              break;
            }
          }
          if (beardY >= 0) {
            break;
          }
        }
        if (beardY >= 0 && beardY < 39) {
          let n = 0;
          for (let x = 24; x <= 42 && n < 3; x++) {
            if (grid[beardY][x] === "h" || grid[beardY][x] === "H" || grid[beardY][x] === "a") {
              plot(grid, x, beardY + 1, ".");
              n++;
            }
          }
        }
        stampHand(leadHandX, leadHandY);
        plot(grid, 40, 38, "s");
        plot(grid, 41, 38, "w");
        plot(grid, 42, 38, "W");
        plot(grid, 31, 35, "h");
        plot(grid, 35, 36, "h");
        for (let y = 36; y <= 37; y++) {
          for (let x = 28; x <= 30; x++) {
            if (grid[y][x] === ".") {
              plot(grid, x, y, "h");
            }
          }
        }
        for (let y = 34; y <= 37; y++) {
          let run = 0;
          for (let x = 31; x <= 35; x++) {
            if (grid[y][x] !== ".") {
              run = 0;
              continue;
            }
            run++;
            if (run > 3) {
              plot(grid, x, y, "h");
              run = 0;
            }
          }
        }
      }
    }
    if ((character === "mina" || character === "dobbs") && reach >= 2 && reach < 3) {
      const cutWrist = (hx: number, hy: number): void => {
        plot(grid, hx - 2, hy, ".");
        plot(grid, hx + 2, hy, ".");
        plot(grid, hx - 2, hy + 1, "k");
        plot(grid, hx + 2, hy + 1, "k");
        fill(grid, hx - 1, hy - 1, hx + 1, hy + 1, "s");
        plot(grid, hx, hy, "d");
      };
      cutWrist(leadHandX, leadHandY);
      cutWrist(rearHandX, rearHandY);
    }
    if (character === "dobbs" && reach >= 2 && reach < 3) {
      for (let y = 33; y <= 50; y++) {
        const xs: Array<number> = [];
        for (let x = 11; x <= 44; x++) {
          if (grid[y][x] === "w" || grid[y][x] === "W") {
            xs.push(x);
          }
        }
        if (xs.length === 0) {
          const t = (y - 33) / 17;
          const wx = Math.round(44 - t * 12);
          plot(grid, wx, y, "w");
          plot(grid, wx + 1, y, "W");
        }
      }
    }
    return freeze(grid);
  };
  const mirrorRows = (rows: ReadonlyArray<string>): ReadonlyArray<string> =>
    rows.map((row) => row.split("").reverse().join(""));
  const SHADE: Readonly<Record<string, string>> = {
    M: "n",
    n: "M",
    m: "N",
    N: "m",
    f: "C",
    C: "f",
    W: "w",
    w: "W",
    h: "H",
    H: "h",
    s: "S",
    S: "s",
    a: "Z",
    Z: "a",
  };
  const relight = (rows: ReadonlyArray<string>): ReadonlyArray<string> =>
    rows.map((row) =>
      row
        .split("")
        .map((mark) => SHADE[mark] ?? mark)
        .join(""),
    );
  const plantR = 56;
  const plantL = 42;
  const digLeft = [
    left(18, 30, 0),
    left(10, 40, 1),
    left(4, 50, 2),
    left(2, 56, 4),
    left(6, 55, 3),
  ];
  return {
    wait: [wait(0), wait(1)].map(sprite),
    ready: [ready(0), ready(2)].map(sprite),
    walk: [
      walk(49, plantL - 2, plantR + 2, 0, 48, false, false, 0, 4, 0),
      walk(46, plantL - 2, plantR + 8, -2, 48, false, false, 0, 7, 0),
      walk(44, plantL + 10, plantR + 2, 0, 48, true, false, 4, 0, 0),
      walk(46, plantL + 12, plantR + 2, -2, 48, true, false, 7, 0, 0),
    ].map(sprite),
    digDown: [
      down(38, 22, -2, false),
      down(40, 36, 0, false),
      down(44, 50, 2, false),
      down(40, 64, 4, true),
      down(40, 59, 1, false),
    ].map(sprite),
    digLeft: digLeft.map(sprite),
    digRight: digLeft.map(mirrorRows).map(relight).map(sprite),
  };
};

export const DIGGERS: Readonly<Record<Character, Frames>> = {
  rusty: frames("rusty"),
  mina: frames("mina"),
  dobbs: frames("dobbs"),
  pip: frames("pip"),
};

const outline = (sprite: Sprite): Sprite => {
  const outside: Array<Array<boolean>> = Array.from({ length: SIZE }, () =>
    Array.from({ length: SIZE }, () => false),
  );
  const queue: Array<readonly [number, number]> = [];
  for (let i = 0; i < SIZE; i++) {
    queue.push([i, 0], [i, SIZE - 1], [0, i], [SIZE - 1, i]);
  }
  while (queue.length > 0) {
    const next = queue.pop();
    if (next === undefined) {
      break;
    }
    const [x, y] = next;
    if (x < 0 || y < 0 || x >= SIZE || y >= SIZE || outside[y][x]) {
      continue;
    }
    if (sprite.rows[y][x] !== ".") {
      continue;
    }
    outside[y][x] = true;
    queue.push([x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]);
  }
  const shovelMark = (mark: string): boolean =>
    mark === "w" || mark === "W" || mark === "M" || mark === "m" || mark === "n" || mark === "N";
  const nearVoid = (x: number, y: number, reach: number): boolean => {
    for (let dy = -reach; dy <= reach; dy++) {
      for (let dx = -reach; dx <= reach; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= SIZE || ny >= SIZE) {
          return true;
        }
        if (outside[ny][nx]) {
          return true;
        }
      }
    }
    return false;
  };
  const grid: Grid = sprite.rows.map((row) => row.split(""));
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (sprite.rows[y][x] === ".") {
        plot(grid, x, y, ".");
        continue;
      }
      if (shovelMark(sprite.rows[y][x])) {
        continue;
      }
      if (!nearVoid(x, y, 1)) {
        plot(grid, x, y, ".");
        continue;
      }
      const hair =
        sprite.rows[y][x] === "h" || sprite.rows[y][x] === "i" || sprite.rows[y][x] === "H";
      if (hair && x < 22 && y > 20) {
        plot(grid, x, y, ".");
        continue;
      }
      const upOut = outside[y - 1]?.[x] ?? true;
      const leftOut = outside[y][x - 1] ?? true;
      const downOut = outside[y + 1]?.[x] ?? true;
      const rightOut = outside[y][x + 1] ?? true;
      if (downOut || rightOut) {
        plot(grid, x, y, "f");
      } else if (upOut && leftOut) {
        plot(grid, x, y, "g");
      } else {
        plot(grid, x, y, "f");
      }
    }
  }
  let shaftX = 0;
  let shaftN = 0;
  let bladeX = 0;
  let bladeY = 0;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (!shovelMark(sprite.rows[y][x])) {
        continue;
      }
      plot(grid, x, y, ".");
      if (sprite.rows[y][x] === "w" || sprite.rows[y][x] === "W") {
        shaftX += x;
        shaftN += 1;
      }
      if (
        (sprite.rows[y][x] === "M" ||
          sprite.rows[y][x] === "m" ||
          sprite.rows[y][x] === "n" ||
          sprite.rows[y][x] === "N") &&
        y >= bladeY
      ) {
        bladeX = x;
        bladeY = y;
      }
    }
  }
  const holdX = shaftN === 0 ? 51 : Math.round(shaftX / shaftN);
  let topY = SIZE;
  let botY = 0;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (sprite.rows[y][x] !== "w" && sprite.rows[y][x] !== "W") {
        continue;
      }
      if (Math.abs(x - holdX) > 3) {
        continue;
      }
      topY = Math.min(topY, y);
      botY = Math.max(botY, y);
    }
  }
  if (topY < botY) {
    fill(grid, holdX, topY, holdX + 1, botY, "g");
  }
  fill(grid, Math.max(0, bladeX), Math.max(0, bladeY - 2), bladeX + 1, bladeY, "g");
  fill(grid, holdX, Math.min(botY, bladeY), holdX + 1, Math.max(botY, bladeY), "g");
  fill(grid, 53, 68, 53, 72, "g");
  plot(grid, 54, 73, "g");
  plot(grid, 55, 74, "g");
  const joinX = Math.min(holdX, bladeX);
  const joinX1 = Math.max(holdX + 1, bladeX);
  const joinY = Math.min(botY + 1, bladeY);
  fill(grid, joinX, joinY, joinX1, Math.min(SIZE - 1, joinY + 1), "g");
  const toolBridges: ReadonlyArray<readonly [number, number]> = [
    [51, 53],
    [42, 41],
    [51, 37],
    [44, 40],
    [23, 72],
    [24, 72],
    [25, 72],
  ];
  for (const [bx, by] of toolBridges) {
    if ((sprite.rows[by]?.[bx] ?? ".") !== ".") {
      plot(grid, bx, by, "g");
    }
  }
  plot(grid, holdX, topY, "l");
  let eyeX = SIZE;
  let eyeY = SIZE;
  for (let y = 8; y < 50; y++) {
    for (let x = 12; x < 48; x++) {
      if (sprite.rows[y][x] === "t" && x < eyeX) {
        eyeX = x;
        eyeY = y;
      }
    }
  }
  if (eyeX < SIZE) {
    plot(grid, eyeX, eyeY, "l");
    if (sprite.rows[eyeY][eyeX + 1] === "t") {
      plot(grid, eyeX + 1, eyeY, "l");
    }
    if (sprite.rows[eyeY + 1]?.[eyeX] === "t") {
      plot(grid, eyeX, eyeY + 1, "l");
    }
    if (sprite.rows[eyeY + 1]?.[eyeX + 1] === "t") {
      plot(grid, eyeX + 1, eyeY + 1, "l");
    }
  }
  const stampAccent = (y0: number, y1: number, want: ReadonlySet<string>): void => {
    let bestX = SIZE;
    let bestY = SIZE;
    for (let y = y0; y <= y1; y++) {
      for (let x = 0; x < SIZE; x++) {
        if (grid[y][x] === ".") {
          continue;
        }
        if (!want.has(sprite.rows[y][x])) {
          continue;
        }
        const upOut = outside[y - 1]?.[x] ?? true;
        const leftOut = outside[y][x - 1] ?? true;
        if (!(upOut && leftOut)) {
          continue;
        }
        if (x < bestX || (x === bestX && y < bestY)) {
          bestX = x;
          bestY = y;
        }
      }
    }
    if (bestX < SIZE) {
      plot(grid, bestX, bestY, "l");
      plot(grid, Math.min(SIZE - 1, bestX + 1), bestY, "l");
      if (bestY + 1 < SIZE && grid[bestY + 1][bestX] !== ".") {
        plot(grid, bestX, bestY + 1, "l");
        plot(grid, Math.min(SIZE - 1, bestX + 1), bestY + 1, "l");
      }
    }
  };
  stampAccent(48, 56, new Set(["f", "c", "e", "C"]));
  stampAccent(58, 70, new Set(["p", "q"]));
  const headMark = (mark: string): boolean =>
    mark === "h" || mark === "i" || mark === "H" || mark === "s" || mark === "d" || mark === "t";
  for (let y = 5; y <= 47; y++) {
    for (let x = 12; x <= 45; x++) {
      if (!headMark(sprite.rows[y][x])) {
        continue;
      }
      if (grid[y][x] !== ".") {
        continue;
      }
      if (!nearVoid(x, y, 2)) {
        continue;
      }
      const upOut = outside[y - 1]?.[x] ?? true;
      const leftOut = outside[y][x - 1] ?? true;
      plot(grid, x, y, upOut && leftOut ? "g" : "f");
    }
  }
  let hair = 0;
  for (let y = 4; y <= 26 && hair < 12; y++) {
    for (let x = 12; x <= 48 && hair < 12; x++) {
      const mark = sprite.rows[y][x];
      if (mark !== "h" && mark !== "i" && mark !== "H") {
        continue;
      }
      if (grid[y][x] === ".") {
        continue;
      }
      plot(grid, x, y, "g");
      hair += 1;
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "l") {
        continue;
      }
      if (shovelMark(sprite.rows[y][x]) || sprite.rows[y][x] === "t") {
        continue;
      }
      const downOut = outside[y + 1]?.[x] ?? true;
      const rightOut = outside[y][x + 1] ?? true;
      const upOut = outside[y - 1]?.[x] ?? true;
      const leftOut = outside[y][x - 1] ?? true;
      if ((downOut || rightOut) && !(upOut && leftOut)) {
        plot(grid, x, y, "g");
      }
    }
  }
  const bodyUL = (x: number, y: number): boolean => {
    const upOut = (sprite.rows[y - 1]?.[x] ?? ".") === "." || y === 0;
    const leftOut = (sprite.rows[y][x - 1] ?? ".") === "." || x === 0;
    return upOut && leftOut;
  };
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "l" || bodyUL(x, y)) {
        continue;
      }
      if (sprite.rows[y][x] === "t" || shovelMark(sprite.rows[y][x])) {
        continue;
      }
      plot(grid, x, y, "g");
    }
  }
  const rimSpots: ReadonlyArray<readonly [number, number]> = [
    [18, 34],
    [22, 48],
    [28, 62],
  ];
  for (const [rx, ry] of rimSpots) {
    if (grid[ry]?.[rx] && grid[ry][rx] !== "." && bodyUL(rx, ry)) {
      plot(grid, rx, ry, "l");
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "l") {
        continue;
      }
      const upOut = (sprite.rows[y - 1]?.[x] ?? ".") === "." || y === 0;
      const leftOut = (sprite.rows[y][x - 1] ?? ".") === "." || x === 0;
      const downOut = (sprite.rows[y + 1]?.[x] ?? ".") === "." || y === SIZE - 1;
      const rightOut = (sprite.rows[y][x + 1] ?? ".") === "." || x === SIZE - 1;
      if ((downOut || rightOut) && !(upOut && leftOut) && sprite.rows[y][x] !== "t") {
        plot(grid, x, y, "g");
      }
    }
  }
  let toolLit = false;
  for (let y = 48; y < 70 && !toolLit; y++) {
    for (let x = 0; x < SIZE && !toolLit; x++) {
      if (!shovelMark(sprite.rows[y][x])) {
        continue;
      }
      const downOut = (sprite.rows[y + 1]?.[x] ?? ".") === ".";
      const rightOut = (sprite.rows[y][x + 1] ?? ".") === ".";
      if (downOut || rightOut) {
        continue;
      }
      plot(grid, x, y, "l");
      toolLit = true;
    }
  }
  let extra = 0;
  for (let y = 48; y < 64 && extra < 2; y += 8) {
    for (let x = 14; x < 40 && extra < 2; x++) {
      if (grid[y][x] === "." || grid[y][x] === "l") {
        continue;
      }
      if (!bodyUL(x, y) || shovelMark(sprite.rows[y][x])) {
        continue;
      }
      plot(grid, x, y, "l");
      extra += 1;
      break;
    }
  }
  let shoulder = false;
  for (let y = 48; y <= 56 && !shoulder; y++) {
    for (let x = 10; x <= 44 && !shoulder; x++) {
      if (grid[y][x] === "." || shovelMark(sprite.rows[y][x])) {
        continue;
      }
      const downOut = (sprite.rows[y + 1]?.[x] ?? ".") === ".";
      const rightOut = (sprite.rows[y][x + 1] ?? ".") === ".";
      if (downOut || rightOut) {
        continue;
      }
      plot(grid, x, y, "l");
      plot(grid, Math.min(SIZE - 1, x + 1), y, "l");
      shoulder = true;
    }
  }
  stampAccent(70, 76, new Set(["b", "B"]));
  for (let y = 8; y <= 77; y++) {
    for (let x = 12; x <= 60; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      if (grid[y][x + 3] === "." || grid[y][x + 1] !== "." || grid[y][x + 2] !== ".") {
        continue;
      }
      if (sprite.rows[y][x + 1] === "." || sprite.rows[y][x + 2] === ".") {
        continue;
      }
      if (!nearVoid(x + 1, y, 1) && !nearVoid(x + 2, y, 1)) {
        continue;
      }
      plot(grid, x + 1, y, grid[y][x]);
      plot(grid, x + 2, y, grid[y][x]);
    }
  }
  for (let y = 8; y <= 77; y++) {
    for (let x = 12; x <= 60; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      if (grid[y + 3]?.[x] === "." || grid[y + 1]?.[x] !== "." || grid[y + 2]?.[x] !== ".") {
        continue;
      }
      if ((sprite.rows[y + 1]?.[x] ?? ".") === "." || (sprite.rows[y + 2]?.[x] ?? ".") === ".") {
        continue;
      }
      if (!nearVoid(x, y + 1, 1) && !nearVoid(x, y + 2, 1)) {
        continue;
      }
      plot(grid, x, y + 1, grid[y][x]);
      plot(grid, x, y + 2, grid[y][x]);
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      if (x >= 66 && x <= 67 && y >= 16 && y <= 17) {
        continue;
      }
      if (sprite.rows[y][x] === "t" || shovelMark(sprite.rows[y][x])) {
        continue;
      }
      const n =
        (grid[y][x - 1] !== "." && grid[y][x - 1] !== undefined ? 1 : 0) +
        (grid[y][x + 1] !== "." && grid[y][x + 1] !== undefined ? 1 : 0) +
        (grid[y - 1]?.[x] !== "." && grid[y - 1]?.[x] !== undefined ? 1 : 0) +
        (grid[y + 1]?.[x] !== "." && grid[y + 1]?.[x] !== undefined ? 1 : 0);
      if (n === 0) {
        plot(grid, x, y, ".");
      }
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "l") {
        continue;
      }
      const lit =
        grid[y][x - 1] === "l" ||
        grid[y][x + 1] === "l" ||
        grid[y - 1]?.[x] === "l" ||
        grid[y + 1]?.[x] === "l";
      if (lit) {
        continue;
      }
      if (sprite.rows[y][x] === "t" || shovelMark(sprite.rows[y][x])) {
        plot(grid, x, Math.min(SIZE - 1, y + 1), "l");
        plot(grid, Math.min(SIZE - 1, x + 1), y, "l");
        continue;
      }
      plot(grid, x, y, "g");
    }
  }
  const bodyBridges: ReadonlyArray<readonly [number, number]> = [
    [22, 57],
    [22, 58],
    [23, 72],
    [24, 72],
    [25, 72],
  ];
  for (const [bx, by] of bodyBridges) {
    if ((grid[by]?.[bx] ?? ".") !== ".") {
      plot(grid, bx, by, "g");
    }
  }
  fill(grid, 27, 38, 28, 39, ".");
  const rim: Array<readonly [number, number]> = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      const open =
        (outside[y][x - 1] ?? true) ||
        (outside[y][x + 1] ?? true) ||
        (outside[y - 1]?.[x] ?? true) ||
        (outside[y + 1]?.[x] ?? true);
      if (open) {
        rim.push([x, y]);
      }
    }
  }
  const kept: Array<Array<boolean>> = grid.map((row) => row.map(() => false));
  const keepQ = [...rim];
  while (keepQ.length > 0) {
    const next = keepQ.pop();
    if (next === undefined) {
      break;
    }
    const [x, y] = next;
    if (y < 0 || y >= SIZE || x < 0 || x >= SIZE || kept[y][x] || grid[y][x] === ".") {
      continue;
    }
    kept[y][x] = true;
    keepQ.push([x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]);
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "." && !kept[y][x]) {
        plot(grid, x, y, ".");
      }
    }
  }
  fill(grid, 27, 38, 28, 39, ".");
  for (let y = 0; y < 28; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === "l") {
        plot(grid, x, y, "g");
      }
    }
  }
  let eyes = 0;
  for (let y = 8; y < 48 && eyes < 2; y++) {
    for (let x = 12; x < 48 && eyes < 2; x++) {
      if (sprite.rows[y][x] !== "t") {
        continue;
      }
      plot(grid, x, y, "l");
      eyes += 1;
    }
  }
  const growIn = (): void => {
    const rimInk = freeze(grid);
    for (let y = 1; y < SIZE - 1; y++) {
      for (let x = 1; x < SIZE - 1; x++) {
        if (sprite.rows[y][x] === "." || shovelMark(sprite.rows[y][x])) {
          continue;
        }
        if (rimInk[y][x] !== ".") {
          continue;
        }
        const n =
          (rimInk[y][x - 1] !== "." ? 1 : 0) +
          (rimInk[y][x + 1] !== "." ? 1 : 0) +
          (rimInk[y - 1][x] !== "." ? 1 : 0) +
          (rimInk[y + 1][x] !== "." ? 1 : 0);
        if (n < 2) {
          continue;
        }
        plot(grid, x, y, "g");
      }
    }
  };
  growIn();
  for (let y = 0; y < SIZE - 1; y++) {
    for (let x = 0; x < SIZE - 1; x++) {
      if (grid[y][x] !== "g") {
        continue;
      }
      if (shovelMark(sprite.rows[y][x])) {
        continue;
      }
      const diag = grid[y + 1][x + 1] === "g";
      const east = grid[y][x + 1] === "g";
      const south = grid[y + 1][x] === "g";
      if (diag && !east && !south) {
        plot(grid, x, y, ".");
      }
    }
  }
  if (topY < botY) {
    fill(grid, holdX, topY, holdX + 1, botY, "g");
  }
  fill(grid, Math.max(0, bladeX), Math.max(0, bladeY - 2), bladeX + 1, bladeY, "g");
  fill(grid, holdX, Math.min(botY, bladeY), holdX + 1, Math.max(botY, bladeY), "g");
  fill(grid, joinX, joinY, joinX1, Math.min(SIZE - 1, joinY + 1), "g");
  fill(grid, 53, 68, 53, 72, "g");
  plot(grid, 54, 73, "g");
  plot(grid, 55, 74, "g");
  plot(grid, 56, 76, "g");
  for (let x = 52; x <= 57; x++) {
    plot(grid, x, 65, ".");
  }
  for (let y = 66; y <= 76; y++) {
    plot(grid, 57, y, ".");
  }
  const stair: ReadonlyArray<readonly [number, number]> = [
    [52, 66],
    [53, 68],
    [53, 70],
    [54, 72],
    [55, 74],
    [56, 76],
  ];
  for (const [tx, ty] of stair) {
    plot(grid, tx, ty, "g");
  }
  plot(grid, 52, 65, "g");
  plot(grid, 51, 65, "g");
  for (let x = 54; x <= 57; x++) {
    plot(grid, x, 65, ".");
  }
  for (let y = 66; y <= 75; y++) {
    plot(grid, 57, y, ".");
  }
  const keptAfter: Array<Array<boolean>> = grid.map((row) => row.map(() => false));
  const keepAfter: Array<readonly [number, number]> = [];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      const open =
        (outside[y][x - 1] ?? true) ||
        (outside[y][x + 1] ?? true) ||
        (outside[y - 1]?.[x] ?? true) ||
        (outside[y + 1]?.[x] ?? true) ||
        shovelMark(sprite.rows[y][x]);
      if (open) {
        keepAfter.push([x, y]);
      }
    }
  }
  const afterQ = [...keepAfter];
  while (afterQ.length > 0) {
    const next = afterQ.pop();
    if (next === undefined) {
      break;
    }
    const [x, y] = next;
    if (y < 0 || y >= SIZE || x < 0 || x >= SIZE || keptAfter[y][x] || grid[y][x] === ".") {
      continue;
    }
    keptAfter[y][x] = true;
    afterQ.push([x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]);
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "." && !keptAfter[y][x]) {
        plot(grid, x, y, ".");
      }
    }
  }
  let rimL = 0;
  for (let y = 18; y <= 70 && rimL < 4; y++) {
    for (let x = 12; x <= 48 && rimL < 4; x++) {
      if (grid[y][x] === "." || grid[y][x] === "l" || shovelMark(sprite.rows[y][x])) {
        continue;
      }
      const upOut = (sprite.rows[y - 1]?.[x] ?? ".") === "." || y === 0;
      const leftOut = (sprite.rows[y][x - 1] ?? ".") === "." || x === 0;
      if (!(upOut && leftOut)) {
        continue;
      }
      plot(grid, x, y, "l");
      plot(grid, Math.min(SIZE - 1, x + 1), y, "l");
      rimL += 1;
    }
  }
  fill(grid, 27, 38, 28, 39, ".");
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === "l") {
        plot(grid, x, y, "g");
      }
    }
  }
  let gazeX = SIZE;
  let gazeY = SIZE;
  for (let y = 8; y < 36; y++) {
    for (let x = 12; x < 48; x++) {
      if (sprite.rows[y][x] === "t" && x < gazeX) {
        gazeX = x;
        gazeY = y;
      }
    }
  }
  if (gazeX < SIZE) {
    let nearX = gazeX;
    let nearY = gazeY;
    let best = SIZE * SIZE;
    for (let y = 0; y < 40; y++) {
      for (let x = 0; x < SIZE; x++) {
        if (grid[y][x] === ".") {
          continue;
        }
        const onEdge =
          (outside[y][x - 1] ?? true) ||
          (outside[y][x + 1] ?? true) ||
          (outside[y - 1]?.[x] ?? true) ||
          (outside[y + 1]?.[x] ?? true);
        if (!onEdge) {
          continue;
        }
        const dist = Math.abs(x - gazeX) + Math.abs(y - gazeY);
        if (dist < best) {
          best = dist;
          nearX = x;
          nearY = y;
        }
      }
    }
    line(grid, gazeX, gazeY, nearX, nearY, "g", 1);
    plot(grid, gazeX, gazeY, "l");
    plot(grid, gazeX + 1, gazeY, "l");
  }
  if ((grid[Math.min(SIZE - 1, botY)]?.[holdX] ?? ".") !== ".") {
    plot(grid, holdX, Math.min(SIZE - 1, botY), "l");
    plot(grid, Math.min(SIZE - 1, holdX + 1), Math.min(SIZE - 1, botY), "l");
  }
  if ((grid[48]?.[22] ?? ".") === ".") {
    plot(grid, 22, 48, "g");
    plot(grid, 23, 48, "g");
  }
  const extras: ReadonlyArray<readonly [number, number]> = [
    [18, 34],
    [22, 48],
    [23, 48],
    [28, 62],
  ];
  for (const [ex, ey] of extras) {
    if ((grid[ey]?.[ex] ?? ".") === ".") {
      continue;
    }
    plot(grid, ex, ey, "l");
    if ((grid[ey][ex + 1] ?? ".") !== ".") {
      plot(grid, ex + 1, ey, "l");
    } else if ((grid[ey + 1]?.[ex] ?? ".") !== ".") {
      plot(grid, ex, ey + 1, "l");
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "l") {
        continue;
      }
      const paired =
        grid[y][x - 1] === "l" ||
        grid[y][x + 1] === "l" ||
        grid[y - 1]?.[x] === "l" ||
        grid[y + 1]?.[x] === "l";
      if (paired) {
        continue;
      }
      if ((grid[y][x + 1] ?? ".") !== ".") {
        plot(grid, x + 1, y, "l");
      } else if ((grid[y + 1]?.[x] ?? ".") !== ".") {
        plot(grid, x, y + 1, "l");
      }
    }
  }
  let scarf = 0;
  for (let x = 20; x <= 40; x++) {
    if (sprite.rows[52]?.[x] === "a" || sprite.rows[52]?.[x] === "A") {
      scarf++;
    }
  }
  if (scarf >= 8) {
    for (let y = 32; y <= 48; y++) {
      for (let x = 7; x <= 44; x++) {
        const tuft =
          sprite.rows[y][x] === "h" || sprite.rows[y][x] === "i" || sprite.rows[y][x] === "H";
        if (y >= 38 && x <= 22 && tuft) {
          plot(grid, x, y, ".");
          continue;
        }
        if (y >= 36 && y <= 46 && (x < 27 || x > 37) && !shovelMark(sprite.rows[y][x])) {
          plot(grid, x, y, ".");
        }
        if (y >= 32 && y <= 35 && x < 26 && !shovelMark(sprite.rows[y][x])) {
          plot(grid, x, y, ".");
        }
      }
    }
    fill(grid, 29, 36, 29, 46, "g");
    fill(grid, 35, 36, 35, 46, "g");
    fill(grid, 31, 36, 33, 56, ".");
    for (let pass = 0; pass < 2; pass++) {
      const snap = freeze(grid);
      for (let y = 47; y <= 58; y++) {
        for (let x = 18; x <= 44; x++) {
          if (sprite.rows[y][x] === "." || shovelMark(sprite.rows[y][x])) {
            continue;
          }
          if (snap[y][x] !== ".") {
            continue;
          }
          const n =
            (snap[y][x - 1] !== "." ? 1 : 0) +
            (snap[y][x + 1] !== "." ? 1 : 0) +
            (snap[y - 1]?.[x] !== "." ? 1 : 0) +
            (snap[y + 1]?.[x] !== "." ? 1 : 0);
          if (n >= 1) {
            plot(grid, x, y, "g");
          }
        }
      }
    }
    for (let y = 45; y <= 74; y++) {
      let left = SIZE;
      let right = 0;
      for (let x = 10; x <= 44; x++) {
        if (sprite.rows[y][x] === "." || shovelMark(sprite.rows[y][x])) {
          continue;
        }
        left = Math.min(left, x);
        right = Math.max(right, x);
      }
      if (left < SIZE) {
        plot(grid, left + (y % 2), y, "g");
      }
      if (right > 0 && right !== left + (y % 2)) {
        plot(grid, right - (y % 3 === 1 ? 1 : 0), y, "g");
      }
    }
    for (let y = 36; y <= 64; y += 4) {
      plot(grid, 29, y, ".");
      plot(grid, 35, y, ".");
    }
  }
  const dobbsLike = sprite.rows[54]?.[40] === "A" && sprite.rows[66]?.[28] === "A";
  for (let y = 65; y <= 76; y++) {
    for (let x = 52; x <= 57; x++) {
      if (shovelMark(sprite.rows[y][x])) {
        continue;
      }
      plot(grid, x, y, ".");
    }
  }
  let ghostShaft: readonly [number, number] = [holdX, Math.min(SIZE - 1, botY)];
  let ghostBlade: readonly [number, number] = [bladeX, bladeY];
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      if (sprite.rows[y][x] === "w" || sprite.rows[y][x] === "W") {
        ghostShaft = [x, y];
      }
      if (
        sprite.rows[y][x] === "M" ||
        sprite.rows[y][x] === "m" ||
        sprite.rows[y][x] === "n" ||
        sprite.rows[y][x] === "N"
      ) {
        ghostBlade = [x, y];
      }
    }
  }
  const stairTo = (from: readonly [number, number], to: readonly [number, number]): void => {
    let x = from[0];
    let y = from[1];
    plot(grid, x, y, "g");
    while (x !== to[0] || y !== to[1]) {
      const dx = to[0] - x;
      const dy = to[1] - y;
      if (Math.abs(dx) >= Math.abs(dy) && dx !== 0) {
        x += dx > 0 ? 1 : -1;
      } else {
        y += dy > 0 ? 1 : -1;
      }
      plot(grid, x, y, "g");
    }
  };
  stairTo(ghostShaft, ghostBlade);
  if (grid[65][51] === "l" && grid[65][52] !== "l") {
    plot(grid, 52, 65, "l");
  }
  for (let y = 65; y <= 76; y++) {
    let rimKept = 0;
    for (let x = 52; x <= 57; x++) {
      if (grid[y][x] === ".") {
        continue;
      }
      rimKept++;
      if (rimKept > 2) {
        plot(grid, x, y, ".");
      }
    }
  }
  stairTo(ghostShaft, ghostBlade);
  let capKeep = 0;
  for (let x = 52; x <= 57; x++) {
    if (grid[65][x] === ".") {
      continue;
    }
    capKeep++;
    if (capKeep > 2) {
      plot(grid, x, 65, ".");
    }
  }
  if (dobbsLike) {
    for (let y = 41; y <= 68; y++) {
      for (let x = 9; x <= 50; x++) {
        if (shovelMark(sprite.rows[y][x])) {
          continue;
        }
        const source = sprite.rows[y][x];
        const gap = x >= 24 && x <= 36 && y >= 48 && y <= 62 && source === ".";
        if (source === "." && !gap) {
          continue;
        }
        if (x >= 29 && x <= 38 && y >= 49 && y <= 58 && y !== 54) {
          plot(grid, x, y, ".");
          continue;
        }
        if (x >= 22 && x <= 36 && y >= 46 && y <= 62 && y !== 54 && !shovelMark(source)) {
          if (source === "p" || source === "q" || source === "P" || source === ".") {
            plot(grid, x, y, ".");
            continue;
          }
        }
        if (y === 53 && x >= 30 && x <= 48) {
          plot(grid, x, y, ".");
          continue;
        }
        if (grid[y][x] === ".") {
          plot(grid, x, y, "g");
        }
      }
    }
    plot(grid, 22, 48, "l");
    plot(grid, 23, 48, "l");
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "l") {
        continue;
      }
      const paired =
        grid[y][x - 1] === "l" ||
        grid[y][x + 1] === "l" ||
        grid[y - 1]?.[x] === "l" ||
        grid[y + 1]?.[x] === "l";
      if (paired) {
        continue;
      }
      if ((grid[y][x + 1] ?? ".") !== ".") {
        plot(grid, x + 1, y, "l");
      } else if ((grid[y + 1]?.[x] ?? ".") !== ".") {
        plot(grid, x, y + 1, "l");
      } else {
        plot(grid, x, y, "g");
      }
    }
  }
  if (scarf >= 8) {
    for (let y = 36; y <= 57; y++) {
      for (let x = 12; x <= 19; x++) {
        plot(grid, x, y, ".");
      }
    }
    const tails: ReadonlyArray<readonly [number, number]> = [
      [14, 44],
      [15, 44],
      [14, 45],
      [13, 49],
      [14, 49],
      [13, 50],
      [16, 54],
      [17, 54],
      [17, 55],
    ];
    for (const [tx, ty] of tails) {
      plot(grid, tx, ty, "g");
    }
  }
  return {
    rows: freeze(grid),
    palette: { g: "#4a6578", l: "#9eb6c4", f: "#466277" },
  };
};

const ghosts = new WeakMap<Sprite, Sprite>();

// The page keys its painted canvases by sprite, so an outline must be the same object every frame.
export const ghost = (sprite: Sprite): Sprite => {
  const known = ghosts.get(sprite);
  if (known !== undefined) {
    return known;
  }
  const next = outline(sprite);
  ghosts.set(sprite, next);
  return next;
};

export const TARGET: Sprite = {
  rows: (() => {
    const grid = sheet();
    fill(grid, 0, 0, 14, 2, "X");
    fill(grid, 0, 0, 2, 14, "X");
    fill(grid, SIZE - 15, 0, SIZE - 1, 2, "X");
    fill(grid, SIZE - 3, 0, SIZE - 1, 14, "X");
    fill(grid, 0, SIZE - 3, 14, SIZE - 1, "X");
    fill(grid, 0, SIZE - 15, 2, SIZE - 1, "X");
    fill(grid, SIZE - 15, SIZE - 3, SIZE - 1, SIZE - 1, "X");
    fill(grid, SIZE - 3, SIZE - 15, SIZE - 1, SIZE - 1, "X");
    fill(grid, 1, 1, 10, 1, "x");
    fill(grid, 1, 1, 1, 10, "x");
    return freeze(grid);
  })(),
  palette: { x: "#9ad4f0", X: "#4a7a98" },
};

export type TileArt = {
  readonly kind: string;
  readonly variants: ReadonlyArray<Sprite>;
};

const GROUND: Palette = {
  D: "#b07848",
  E: "#3a1c0c",
  F: "#e8c080",
  Q: "#4a2814",
  o: "#d4a060",
  x: "#2a1810",
  G: "#3d8a32",
  I: "#7ec85a",
  H: "#246028",
  R: "#5a7a30",
  V: "#3f2f44",
  T: "#5a4560",
  U: "#2e2233",
  Y: "#d4e86a",
};

export const CRACK = "x";

const ofGround = (rows: ReadonlyArray<string>): Sprite => ({ rows, palette: GROUND });

const seam = (grid: Grid): void => {
  for (let y = 0; y < SIZE; y++) {
    plot(grid, SIZE - 1, y, grid[y][0]);
  }
  for (let x = 0; x < SIZE; x++) {
    plot(grid, x, SIZE - 1, grid[0][x]);
  }
};

const soilClod = (
  grid: Grid,
  pts: ReadonlyArray<readonly [number, number]>,
  pebble: boolean,
): void => {
  fillPoly(grid, pts, "D");
  const xs = pts.map((pt) => pt[0]);
  const ys = pts.map((pt) => pt[1]);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bot = Math.max(...ys);
  plot(grid, left + 1, top, "F");
  plot(grid, left + 2, top, "F");
  plot(grid, left + 1, top + 1, "F");
  plot(grid, left + 2, top + 1, "E");
  plot(grid, right - 1, bot - 1, "E");
  plot(grid, right, bot - 1, "E");
  plot(grid, right - 1, bot, "E");
  plot(grid, right, bot, "E");
  if (pebble) {
    const mx = Math.round((left + right) / 2);
    const my = Math.round((top + bot) / 2);
    fill(grid, mx, my, mx + 1, my + 1, "o");
    fill(grid, mx, my + 2, mx + 1, my + 2, "E");
  }
};

const crestSod = (
  grid: Grid,
  crest: ReadonlyArray<readonly [number, number]>,
  shift: number,
): void => {
  const ridge = crest.map((pt) => [pt[0] + shift, pt[1]] as const);
  const first = ridge[0];
  const last = ridge[ridge.length - 1];
  const midX = Math.round((first[0] + last[0]) / 2);
  const mound: ReadonlyArray<readonly [number, number]> = [
    ...ridge,
    [last[0] - 1, 8],
    [midX + 2, 9],
    [midX - 3, 10],
    [first[0] + 1, 8],
  ];
  fillPoly(grid, mound, "G");
  for (let y = 3; y <= 4; y++) {
    for (let x = first[0]; x <= last[0]; x++) {
      if (grid[y][x] !== "G") {
        continue;
      }
      if (y === 4 && x % 4 === 3) {
        continue;
      }
      plot(grid, x, y, x % 2 === 0 ? "H" : "E");
    }
  }
  for (const [x, y] of ridge) {
    if (y <= 1) {
      plot(grid, x, 0, "I");
      plot(grid, x + 1, 0, "I");
    }
    plot(grid, x, y, y === 0 ? "I" : "G");
  }
  for (let x = first[0]; x <= last[0]; x++) {
    for (let y = 16; y >= 8; y--) {
      if (grid[y][x] !== "H" && grid[y][x] !== "G") {
        continue;
      }
      plot(grid, x, y, "E");
      break;
    }
  }
};

const grassRows = (variant: number): ReadonlyArray<string> => {
  const grid = sheet();
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      plot(grid, x, y, y < 16 ? "D" : "Q");
    }
  }
  const soilShift = variant === 2 ? 0 : variant;
  const westX = variant === 2 ? 10 : 4 + soilShift;
  soilClod(
    grid,
    [
      [westX, 28],
      [westX + 14, 30],
      [westX + 12, 44],
      [westX - 1, 42],
    ],
    true,
  );
  const midX = variant === 2 ? 18 : 24;
  const midY = variant === 2 ? 36 : 36 + soilShift;
  soilClod(
    grid,
    [
      [midX, midY],
      [midX + 16, midY + 2],
      [midX + 14, midY + 16],
      [midX - 2, midY + 14],
    ],
    false,
  );
  let eastX = 48;
  let eastY = 26;
  if (variant === 1) {
    eastX = 58;
    eastY = 36;
  } else if (variant === 3) {
    eastX = 38;
    eastY = 18;
  }
  soilClod(
    grid,
    [
      [eastX, eastY],
      [eastX + 16, eastY + 2],
      [eastX + 14, eastY + 16],
      [eastX - 2, eastY + 14],
    ],
    true,
  );
  soilClod(
    grid,
    [
      [8, 56],
      [26, 58],
      [24, 74],
      [6, 72],
    ],
    true,
  );
  soilClod(
    grid,
    [
      [36 + soilShift, 58],
      [54, 60],
      [52, 76],
      [34, 74],
    ],
    false,
  );
  let lowEastX = 58;
  let lowEastY = 48;
  if (variant === 1) {
    lowEastX = 62;
    lowEastY = 58;
  } else if (variant === 3) {
    lowEastX = 48;
    lowEastY = 38;
  }
  soilClod(
    grid,
    [
      [lowEastX, lowEastY],
      [lowEastX + 16, lowEastY + 2],
      [lowEastX + 14, lowEastY + 18],
      [lowEastX - 2, lowEastY + 16],
    ],
    true,
  );
  let shift = 0;
  if (variant === 1) {
    shift = -1;
  } else if (variant === 2) {
    shift = 1;
  }
  const lift = variant === 3 ? 1 : 0;
  crestSod(
    grid,
    [
      [3, 7 + lift],
      [7, 2],
      [12, 0],
      [17, 3],
      [23, 6],
    ],
    shift,
  );
  crestSod(
    grid,
    [
      [31, 5],
      [35, 1 + lift],
      [42, 3],
      [48, 0],
      [52, 7],
    ],
    shift,
  );
  crestSod(
    grid,
    [
      [61, 6],
      [66, 2],
      [72, 4],
      [77, 0],
      [79, 5 + lift],
    ],
    shift,
  );
  for (let y = 7; y <= 14; y++) {
    for (let x = 4; x <= 75; x++) {
      if (grid[y][x] !== "D" && grid[y][x] !== "Q") {
        continue;
      }
      const above = grid[y - 1][x];
      if (above !== "G" && above !== "H" && above !== "I" && above !== "E") {
        continue;
      }
      const notch = (x * 3 + y * 5 + variant) % 7;
      if (notch === 0 || notch === 4) {
        plot(grid, x, y, "E");
      } else if (notch === 1 && y <= 11) {
        plot(grid, x, y, "H");
      } else if (notch === 5 && y <= 13) {
        plot(grid, x, y, "E");
      }
    }
  }
  if (variant === 0) {
    plot(grid, 12, 0, "I");
    plot(grid, 13, 0, "I");
    plot(grid, 35, 0, "I");
    plot(grid, 36, 0, "I");
    plot(grid, 48, 0, "I");
    plot(grid, 49, 0, "I");
    plot(grid, 66, 1, "I");
    plot(grid, 72, 1, "G");
    plot(grid, 77, 1, "I");
    plot(grid, 78, 1, "I");
    plot(grid, 3, 7, "G");
    plot(grid, 7, 2, "G");
    fill(grid, 8, 1, 16, 1, "G");
    fill(grid, 32, 1, 42, 1, "G");
    fill(grid, 60, 1, 70, 1, "G");
    fill(grid, 14, 2, 18, 2, "H");
    fill(grid, 40, 2, 45, 2, "H");
    fill(grid, 66, 2, 71, 2, "E");
    fill(grid, 8, 2, 12, 2, "G");
    fill(grid, 32, 2, 38, 2, "G");
    fill(grid, 60, 2, 65, 2, "G");
    plot(grid, 10, 2, "H");
    plot(grid, 34, 2, "E");
    plot(grid, 62, 2, "H");
    plot(grid, 18, 2, "G");
    plot(grid, 44, 2, "G");
    for (const rx of [6, 24, 28, 50, 54, 74]) {
      if (grid[1][rx] === "G") {
        continue;
      }
      plot(grid, rx, 2, "E");
    }
    plot(grid, 20, 1, "D");
    plot(grid, 21, 1, "D");
    plot(grid, 46, 1, "D");
    plot(grid, 47, 1, "D");
    plot(grid, 55, 1, "D");
    plot(grid, 56, 1, "D");
    for (let y = 4; y <= 14; y++) {
      if (y % 3 !== 1) {
        continue;
      }
      for (const x of [23, 31, 52, 61]) {
        if (grid[y][x] === "G" || grid[y][x] === "H") {
          plot(grid, x, y, "E");
        }
      }
    }
    plot(grid, 3, 7, "G");
  }
  const sod = new Set(["G", "I", "Y", "H", "R"]);
  for (let y = 0; y <= 22; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (!sod.has(grid[y][x])) {
        continue;
      }
      if (grid[y][x - 1] === "D") {
        plot(grid, x - 1, y, "E");
      }
      if (grid[y][x + 1] === "D") {
        plot(grid, x + 1, y, "E");
      }
    }
  }
  const roots: ReadonlyArray<readonly [number, number]> = [
    [8, 17],
    [19, 19],
    [27, 16],
    [41, 18],
    [49, 17],
    [58, 19],
    [70, 18],
    [14, 18],
    [36, 20],
    [64, 17],
    [74, 16],
    [5, 16],
  ];
  for (const [rx, ry] of roots) {
    plot(grid, rx + shift, ry, "H");
    plot(grid, rx + shift, ry + 1, "E");
    if ((rx + variant) % 3 === 0) {
      plot(grid, rx + shift - 1, ry + 1, "E");
    }
  }
  for (const x of [26, 28, 54, 56]) {
    for (let y = 5; y <= 20; y++) {
      if (grid[y][x] === "H" || grid[y][x] === "E") {
        plot(grid, x, y, y < 16 ? "D" : "Q");
      }
    }
  }
  if (variant === 0) {
    plot(grid, 16, 6, "G");
    plot(grid, 17, 6, "H");
    plot(grid, 16, 7, "H");
    plot(grid, 3, 7, "G");
  }
  if (variant === 2) {
    const cap = freeze(grid);
    for (let y = 0; y <= 22; y++) {
      for (let x = 31; x <= 52; x++) {
        if (sod.has(cap[y][x])) {
          plot(grid, x, y, y < 16 ? "D" : "Q");
        }
      }
      for (let x = 31; x <= 52; x++) {
        if (sod.has(cap[y][x])) {
          plot(grid, Math.min(SIZE - 2, x + 6), y, cap[y][x]);
        }
      }
    }
  }
  if (variant === 3) {
    const cap = freeze(grid);
    for (let y = 0; y <= 22; y++) {
      for (let x = 61; x <= 78; x++) {
        if (sod.has(cap[y][x])) {
          plot(grid, x, y, y < 16 ? "D" : "Q");
        }
      }
      for (let x = 61; x <= 78; x++) {
        if (sod.has(cap[y][x])) {
          plot(grid, Math.max(55, x - 6), y, cap[y][x]);
        }
      }
    }
  }
  for (let y = 0; y <= 22; y++) {
    const sodEdge = sod.has(grid[y][1]) || sod.has(grid[y][2]);
    if (grid[y][0] === "D" && sodEdge && y % 3 === 0) {
      plot(grid, 0, y, "E");
    }
  }
  for (let y = 0; y < SIZE; y++) {
    plot(grid, SIZE - 1, y, grid[y][0]);
  }
  return freeze(grid);
};

const dirtRows = (variant: number): ReadonlyArray<string> => {
  const grid = sheet();
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      plot(grid, x, y, "Q");
    }
  }
  const shift = variant;
  soilClod(
    grid,
    [
      [6, 7],
      [14, 6],
      [20, 10],
      [17, 21],
      [8, 18],
      [5, 12],
    ],
    true,
  );
  soilClod(
    grid,
    [
      [55, 7],
      [67, 8],
      [66, 16],
      [55, 15],
    ],
    false,
  );
  soilClod(
    grid,
    [
      [0, 44],
      [14, 45],
      [13, 59],
      [0, 58],
    ],
    true,
  );
  if (variant === 3) {
    soilClod(
      grid,
      [
        [5, 44],
        [23, 45],
        [21, 58],
        [6, 56],
      ],
      true,
    );
  } else if (variant === 1) {
    soilClod(
      grid,
      [
        [57, 44],
        [65, 45],
        [64, 51],
        [58, 50],
      ],
      true,
    );
    soilClod(
      grid,
      [
        [68, 49],
        [75, 50],
        [74, 58],
        [69, 57],
      ],
      false,
    );
  } else if (variant === 2) {
    soilClod(
      grid,
      [
        [48, 40],
        [75, 45],
        [74, 58],
        [50, 56],
      ],
      true,
    );
  } else {
    soilClod(
      grid,
      [
        [57, 44],
        [75, 45],
        [74, 58],
        [58, 57],
      ],
      true,
    );
  }
  if (variant === 2) {
    soilClod(
      grid,
      [
        [28, 24],
        [46, 26],
        [44, 40],
        [27, 38],
      ],
      false,
    );
  } else {
    soilClod(
      grid,
      [
        [22 + shift, 24],
        [40, 26],
        [38, 40],
        [21, 38],
      ],
      false,
    );
  }
  soilClod(
    grid,
    [
      [42, 30 + shift],
      [56, 32],
      [54, 46],
      [41, 44],
    ],
    true,
  );
  soilClod(
    grid,
    [
      [16, 60],
      [24, 58],
      [34, 64],
      [30, 76],
      [18, 73],
      [14, 66],
    ],
    true,
  );
  if (variant === 2) {
    soilClod(
      grid,
      [
        [32, 62],
        [48, 64],
        [46, 76],
        [31, 74],
      ],
      false,
    );
    soilClod(
      grid,
      [
        [26, 12],
        [44, 14],
        [42, 24],
        [25, 23],
      ],
      false,
    );
  } else {
    soilClod(
      grid,
      [
        [38 + shift, 62],
        [54, 64],
        [52, 76],
        [37, 74],
      ],
      false,
    );
    soilClod(
      grid,
      [
        [26, 6 + shift],
        [44, 8],
        [42, 18],
        [25, 17],
      ],
      false,
    );
  }
  soilClod(
    grid,
    [
      [8, 28],
      [20, 30],
      [18, 42],
      [6, 40],
    ],
    true,
  );
  fill(grid, 12 + variant, 10, 13 + variant, 11, "o");
  fill(grid, 12 + variant, 12, 13 + variant, 12, "E");
  fill(grid, 10, 16, 13, 16, "o");
  fill(grid, 10, 17, 13, 17, "E");
  fillPoly(
    grid,
    [
      [20, 12],
      [27, 13],
      [26, 16],
      [20, 15],
    ],
    "D",
  );
  fill(grid, 23, 15, 25, 18, "Q");
  fillPoly(
    grid,
    [
      [38, 31],
      [45, 32],
      [44, 35],
      [38, 34],
    ],
    "D",
  );
  fill(grid, 40, 36, 42, 39, "Q");
  fill(grid, 6, 7, 6, 7, "F");
  fill(grid, 6, 8, 6, 9, "E");
  fill(grid, 55, 7, 57, 7, "F");
  fill(grid, 55, 8, 57, 9, "E");
  fill(grid, 8, 46, 11, 48, "E");
  fill(grid, 8, 46, 10, 46, "F");
  fill(grid, 8, 62, 11, 65, "E");
  fill(grid, 8, 62, 10, 62, "F");
  fill(grid, 62, 62, 65, 65, "E");
  fill(grid, 62, 62, 64, 62, "F");
  plot(grid, 55, 8, "E");
  for (let y = 0; y < SIZE; y++) {
    let run = 0;
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === "E" || grid[y][x] === "F") {
        run++;
        if (run >= 8) {
          plot(grid, x, y, "D");
          run = 0;
        }
      } else {
        run = 0;
      }
    }
  }
  for (let y = 0; y < SIZE; y++) {
    let soil = 0;
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] === "D" || grid[y][x] === "o") {
        soil++;
        if (soil >= 18) {
          plot(grid, x, y, "Q");
          soil = 0;
        }
      } else {
        soil = 0;
      }
    }
  }
  for (let y = 19; y <= 68; y++) {
    for (let x = 7; x <= 74; x++) {
      if (grid[y][x] !== "D" || grid[y][x + 1] !== "D" || grid[y][x + 2] !== "D") {
        continue;
      }
      if (
        grid[y + 1]?.[x] !== "D" ||
        grid[y + 1]?.[x + 1] !== "D" ||
        grid[y + 1]?.[x + 2] !== "D"
      ) {
        continue;
      }
      if (
        grid[y + 2]?.[x] !== "D" ||
        grid[y + 2]?.[x + 1] !== "D" ||
        grid[y + 2]?.[x + 2] !== "D"
      ) {
        continue;
      }
      plot(grid, x + 1, y + 1, "Q");
    }
  }
  for (let y = 19; y <= 68; y++) {
    for (let x = 7; x <= 74; x++) {
      if (grid[y][x] !== "D" || grid[y][x + 1] !== "D") {
        continue;
      }
      if (grid[y + 1]?.[x] !== "D" || grid[y + 1]?.[x + 1] !== "D") {
        continue;
      }
      plot(grid, x + 1, y, "Q");
    }
  }
  for (let y = 19; y <= 68; y++) {
    for (let x = 7; x <= 74; x++) {
      if (grid[y][x] !== "F") {
        continue;
      }
      if (grid[y][x - 1] !== "F" || grid[y][x + 1] !== "F") {
        continue;
      }
      plot(grid, x, y, "Q");
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "F") {
        continue;
      }
      const onMass =
        grid[y + 1]?.[x] === "E" ||
        grid[y][x + 1] === "E" ||
        grid[y + 1]?.[x + 1] === "E" ||
        grid[y][x - 1] === "E" ||
        grid[y - 1]?.[x] === "E";
      if (!onMass) {
        plot(grid, x, Math.min(SIZE - 1, y + 1), "E");
      }
    }
  }
  for (let y = 0; y < SIZE - 2; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "o") {
        continue;
      }
      const under = grid[y + 1][x];
      if (under === "E" || under === "F") {
        continue;
      }
      if (under !== "o") {
        plot(grid, x, y + 1, "E");
        continue;
      }
      if (grid[y + 2][x] !== "E" && grid[y + 2][x] !== "F" && grid[y + 2][x] !== "o") {
        plot(grid, x, y + 2, "E");
      }
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "E" && grid[y][x] !== "F") {
        continue;
      }
      const huddled =
        grid[y - 1]?.[x] === "E" ||
        grid[y - 1]?.[x] === "F" ||
        grid[y + 1]?.[x] === "E" ||
        grid[y + 1]?.[x] === "F" ||
        grid[y][x - 1] === "E" ||
        grid[y][x - 1] === "F" ||
        grid[y][x + 1] === "E" ||
        grid[y][x + 1] === "F";
      if (!huddled) {
        plot(grid, x, y, "Q");
      }
    }
  }
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "D" && grid[y][x] !== "o") {
        continue;
      }
      const huddled =
        grid[y - 1]?.[x] === "D" ||
        grid[y - 1]?.[x] === "o" ||
        grid[y + 1]?.[x] === "D" ||
        grid[y + 1]?.[x] === "o" ||
        grid[y][x - 1] === "D" ||
        grid[y][x - 1] === "o" ||
        grid[y][x + 1] === "D" ||
        grid[y][x + 1] === "o";
      if (!huddled) {
        plot(grid, x, y, "Q");
      }
    }
  }
  for (let y = 0; y < SIZE - 2; y++) {
    for (let x = 0; x < SIZE; x++) {
      if (grid[y][x] !== "o") {
        continue;
      }
      const umber =
        grid[y + 1][x] === "E" ||
        grid[y + 1][x] === "F" ||
        grid[y + 1][x + 1] === "E" ||
        grid[y + 2][x] === "E" ||
        grid[y + 2][x] === "F" ||
        grid[y][x + 1] === "E" ||
        grid[y][x + 2] === "E";
      if (!umber) {
        if (grid[y + 1][x] === "o") {
          plot(grid, x, y + 2, "E");
        } else {
          plot(grid, x, y + 1, "E");
        }
      }
    }
  }
  let clodBoxes = 0;
  for (let y = 0; y < SIZE - 1; y++) {
    for (let x = 0; x < SIZE - 1; x++) {
      if (
        grid[y][x] === "o" &&
        grid[y][x + 1] === "o" &&
        grid[y + 1][x] === "o" &&
        grid[y + 1][x + 1] === "o"
      ) {
        clodBoxes++;
        if (clodBoxes > 6) {
          plot(grid, x + 1, y + 1, "E");
        }
      }
    }
  }
  for (let y = 19; y <= 67; y++) {
    for (let x = 7; x <= 73; x++) {
      if (grid[y][x] !== "D" || grid[y][x + 1] !== "D" || grid[y][x + 2] !== "D") {
        continue;
      }
      if (grid[y + 1][x] !== "D" || grid[y + 1][x + 1] !== "D" || grid[y + 1][x + 2] !== "D") {
        continue;
      }
      if (grid[y + 2][x] !== "D" || grid[y + 2][x + 1] !== "D" || grid[y + 2][x + 2] !== "D") {
        continue;
      }
      plot(grid, x + 1, y + 1, "Q");
    }
  }
  if (variant === 2) {
    for (let y = 40; y <= 61; y++) {
      let run = 0;
      for (let x = 46; x <= 77; x++) {
        if (grid[y][x] === "D") {
          run++;
          if (run >= 6) {
            plot(grid, x - 1, y, "Q");
            plot(grid, x, y, "Q");
            plot(grid, x - 1, y + 1, "o");
            plot(grid, x, y + 1, "Q");
            run = 0;
          }
        } else {
          run = 0;
        }
      }
    }
    for (let x = 46; x <= 77; x++) {
      let run = 0;
      for (let y = 40; y <= 61; y++) {
        if (grid[y][x] === "D") {
          run++;
          if (run >= 6) {
            plot(grid, x, y - 1, "Q");
            plot(grid, x, y, "Q");
            plot(grid, x + 1, y - 1, "o");
            plot(grid, x + 1, y, "Q");
            run = 0;
          }
        } else {
          run = 0;
        }
      }
    }
    for (let y = 40; y <= 61; y++) {
      for (let x = 46; x <= 75; x++) {
        if (grid[y][x] === "D" && grid[y][x + 1] === "Q" && grid[y][x + 2] === "D") {
          plot(grid, x + 2, y, "Q");
        }
      }
    }
  }
  seam(grid);
  return freeze(grid);
};

export const CAVE: Sprite = {
  rows: (() => {
    const grid = sheet();
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        const n = (x * 3 + y * 5) % 11;
        let cave = "V";
        if (n < 2) {
          cave = "U";
        } else if (n < 4) {
          cave = "T";
        }
        plot(grid, x, y, cave);
      }
    }
    fill(grid, 0, 0, SIZE - 1, 3, "x");
    return freeze(grid);
  })(),
  palette: GROUND,
};

export const TILES: Readonly<Record<string, TileArt>> = {
  grass: {
    kind: "grass",
    variants: [0, 1, 2, 3].map((variant) => ofGround(grassRows(variant))),
  },
  dirt: {
    kind: "dirt",
    variants: [0, 1, 2, 3].map((variant) => ofGround(dirtRows(variant))),
  },
};

const cavity = (grid: Grid, x: number, y: number, w: number, h: number): void => {
  fill(grid, x, y, x + w - 1, y + h - 1, "x");
  plot(grid, x + w - 1, y, ".");
  plot(grid, x, y + h - 1, ".");
};

const fissure = (
  grid: Grid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  thick0: number,
  thick1 = thick0,
): void => {
  let x = x0;
  let y = y0;
  const paint = (px: number, py: number): void => {
    const hair = thick0 === 1 && thick1 === 1;
    const thick = hair ? 1 : Math.max(1, Math.round((thick0 + thick1) / 2));
    fill(grid, px, py, px + thick - 1, py + thick - 1, "x");
  };
  paint(x, y);
  let lastAxis = "";
  while (x !== x1 || y !== y1) {
    const remainX = x1 - x;
    const remainY = y1 - y;
    const canX = remainX !== 0;
    const canY = remainY !== 0;
    let preferX = canX && !canY;
    if (canX && canY) {
      if (lastAxis === "x") {
        preferX = false;
      } else if (lastAxis === "y") {
        preferX = true;
      } else {
        preferX = Math.abs(remainX) >= Math.abs(remainY);
      }
    }
    const run = preferX
      ? Math.min(3, Math.abs(remainX))
      : Math.min(3, Math.max(1, Math.abs(remainY)));
    let stepX = 0;
    let stepY = 0;
    if (preferX) {
      stepX = remainX > 0 ? 1 : -1;
    } else if (remainY > 0) {
      stepY = 1;
    } else {
      stepY = -1;
    }
    lastAxis = preferX ? "x" : "y";
    for (let k = 0; k < run && (x !== x1 || y !== y1); k++) {
      x += stepX;
      y += stepY;
      paint(x, y);
    }
  }
};

const crackStageRows = (stage: number): ReadonlyArray<string> => {
  const grid = sheet();
  fissure(grid, 40, 14, 39, 22, 1, 1);
  fissure(grid, 39, 22, 41, 36, 1, 1);
  cavity(grid, 38, 22, 2, 2);
  fill(grid, 39, 18, 41, 20, "x");
  plot(grid, 41, 36, "x");
  plot(grid, 37, 15, "x");
  plot(grid, 36, 16, "x");
  plot(grid, 38, 15, "x");
  for (const y of [17, 19, 21, 24, 26, 28, 31, 34]) {
    if (grid[y][39] === ".") {
      plot(grid, 39, y, "x");
    }
  }
  if (stage >= 1) {
    fissure(grid, 41, 36, 32, 48, 1, 1);
    fissure(grid, 39, 22, 52, 16, 1, 1);
    cavity(grid, 32, 48, 2, 2);
    plot(grid, 52, 16, "x");
    plot(grid, 35, 35, "x");
    plot(grid, 50, 18, "x");
    cavity(grid, 35, 35, 2, 2);
  }
  if (stage >= 2) {
    fissure(grid, 32, 48, 29, 64, 1, 1);
    cavity(grid, 29, 64, 2, 2);
    plot(grid, 29, 56, "x");
    fill(grid, 33, 50, 34, 51, "x");
    fill(grid, 31, 54, 32, 55, "x");
    for (let y = 27; y <= 65; y++) {
      if (y === 41 || y === 53) {
        continue;
      }
      let sx = 40;
      if (y >= 38 && y <= 48) {
        sx = 39;
      } else if (y >= 56) {
        sx = 41;
      }
      plot(grid, sx, y, "x");
    }
    fill(grid, 32, 48, 35, 48, "x");
    fill(grid, 35, 49, 38, 49, "x");
    fill(grid, 38, 50, 40, 50, "x");
  }
  if (stage >= 3) {
    fissure(grid, 29, 64, 14, 74, 1, 1);
    fissure(grid, 42, 36, 62, 40, 1, 1);
    fissure(grid, 62, 40, 70, 52, 1, 1);
    fissure(grid, 29, 56, 10, 66, 1, 1);
    plot(grid, 14, 74, "x");
    plot(grid, 70, 52, "x");
    plot(grid, 10, 66, "x");
    fill(grid, 16, 72, 17, 73, "X");
    plot(grid, 47, 49, ".");
    const skip: ReadonlyArray<readonly [number, number]> = [
      [21, 59],
      [17, 63],
      [12, 72],
      [48, 37],
      [61, 44],
      [67, 50],
    ];
    for (const [gx, gy] of skip) {
      if (grid[gy][gx] === "x") {
        plot(grid, gx, gy, ".");
      }
    }
  }
  const boxes: ReadonlyArray<readonly [number, number, number, number]> = [
    [8, 24, 18, 61],
    [57, 8, 72, 49],
    [63, 68, 69, 69],
  ];
  for (const box of boxes) {
    for (let y = box[1]; y <= box[3]; y++) {
      for (let x = box[0]; x <= box[2]; x++) {
        if (grid[y][x] !== "x") {
          continue;
        }
        const n =
          (grid[y][x - 1] === "x" || grid[y][x - 1] === "X" ? 1 : 0) +
          (grid[y][x + 1] === "x" || grid[y][x + 1] === "X" ? 1 : 0) +
          (grid[y - 1]?.[x] === "x" || grid[y - 1]?.[x] === "X" ? 1 : 0) +
          (grid[y + 1]?.[x] === "x" || grid[y + 1]?.[x] === "X" ? 1 : 0);
        if (n === 0) {
          plot(grid, x, y, ".");
        }
      }
    }
  }
  return freeze(grid);
};

const CRACK_PALETTE = { x: "#1c120a", X: "#2a1810" };
const crackStages = [0, 1, 2, 3].map((stage) => crackStageRows(stage).map((row) => row.split("")));
{
  const last = crackStages[crackStages.length - 1];
  for (let y = 0; y < SIZE; y++) {
    let x = 0;
    while (x < SIZE) {
      if (last[y][x] !== "x") {
        x += 1;
        continue;
      }
      let end = x;
      while (end < SIZE && last[y][end] === "x") {
        end += 1;
      }
      if (end - x > 4) {
        for (let i = x + 3; i < end - 1; i += 3) {
          for (const stage of crackStages) {
            if (stage[y][i] === "x" || stage[y][i] === "X") {
              stage[y][i] = ".";
            }
          }
        }
      }
      x = end;
    }
  }
}
{
  const nicks: Array<readonly [number, number]> = [];
  const base = crackStages[0];
  for (let y = 14; y <= 36 && nicks.length < 9; y += 2) {
    for (let x = 36; x <= 46 && nicks.length < 9; x++) {
      if (base[y][x] !== "x") {
        continue;
      }
      if (base[y][x + 1] !== ".") {
        continue;
      }
      nicks.push([x + 1, y]);
      break;
    }
  }
  for (const grid of crackStages) {
    for (const [lx, ly] of nicks) {
      if (grid[ly][lx] === ".") {
        grid[ly][lx] = "X";
      }
    }
  }
}
for (const grid of crackStages.slice(2)) {
  for (let x = 32; x <= 35; x++) {
    grid[48][x] = "x";
  }
  for (let x = 35; x <= 38; x++) {
    grid[49][x] = "x";
  }
  for (let x = 38; x <= 40; x++) {
    grid[50][x] = "x";
  }
}
{
  const last = crackStages[3];
  for (const y of [59, 64, 71]) {
    for (let x = 10; x <= 29; x++) {
      for (const grid of crackStages) {
        if (grid[y][x] === "x" || grid[y][x] === "X") {
          grid[y][x] = ".";
        }
      }
    }
  }
  last[66][10] = "x";
  last[74][14] = "x";
  last[52][70] = "x";
  last[72][16] = "X";
  last[72][17] = "X";
  last[73][16] = "X";
  last[73][17] = "X";
  for (let y = 36; y <= 52; y++) {
    for (let x = 42; x <= 70; x++) {
      if (last[y][x] !== "x" && last[y][x] !== "X") {
        continue;
      }
      const west = last[y][x - 1] === ".";
      const east = last[y][x + 1] === ".";
      if (!west || !east) {
        continue;
      }
      for (const grid of crackStages) {
        if (grid[y][x] === "x" || grid[y][x] === "X") {
          grid[y][x] = ".";
        }
      }
    }
  }
  last[52][70] = "x";
  for (let x = 42; x <= 70; x++) {
    if (last[46][x] === "x" || last[46][x] === "X") {
      last[46][x] = ".";
    }
  }
  for (const x of [45, 54, 63]) {
    if (last[40][x] === "x") {
      last[40][x] = ".";
    }
  }
  last[40][48] = "x";
  last[40][49] = "x";
  last[40][50] = "x";
  for (const x of [52, 53, 58, 59]) {
    last[40][x] = ".";
  }
  last[40][55] = "x";
  last[40][56] = "x";
  last[40][57] = "x";
}
export const CRACKS: ReadonlyArray<Sprite> = crackStages.map((grid) => ({
  rows: freeze(grid),
  palette: CRACK_PALETTE,
}));

export const crackStage = (health: number, maxHealth: number): number => {
  const lost = 1 - health / maxHealth;
  return Math.min(CRACKS.length - 1, Math.floor(lost * CRACKS.length));
};
